import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import type {
  KanbanCardData,
  KanbanCardPatchPayload,
  KanbanCardComment,
  KanbanColumn,
  ArchivedCard,
  ArchivedList,
  KanbanLabel,
  BoardMember,
  KanbanCardCommentAttachment,
} from 'types/kanban';
import { useActiveCompanyStore } from 'stores/activeCompanyStore';
import { useUserProfileStore } from 'stores/userProfileStore';
import { useCompanyServiceQueries } from 'services/company';
import {
  fetchBoards,
  createBoard,
  updateBoard,
  deleteBoard,
  fetchLists,
  createList,
  updateList,
  reorderLists,
  copyList,
  archiveList,
  fetchCards,
  createCard,
  updateCard,
  reorderCardsInList,
  moveCardInBoard,
  addCardComment,
  archiveCard,
  fetchLabels,
  createLabel,
  updateLabel,
  deleteLabel as deleteLabelApi,
  fetchMembers,
  addBoardMember,
  removeBoardMember,
  fetchArchive,
  restoreArchivedCard,
  restoreArchivedList,
  deleteArchivedCard,
  deleteArchivedList,
  buildColumnsFromListsAndCards,
  apiCardsToRecord,
  type ApiBoard,
} from 'services/kanban';
import { ROUTES } from 'utils/constants';
import { clearKanbanOfflineCache } from 'utils/offlineSyncUtils';
import {
  createProximityCollisionDetection,
  findColumnId,
  generateId,
  generateUuidV4,
  getColumnEdgeRole,
  isDragCenterClearlyInColumn,
  isTouchDragActivator,
  scrollBoardToColumn,
} from './kanbanUtils';
import { useKanbanSocket, listPayloadToColumn } from './useKanbanSocket';

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

interface MoveWithinBoardRequest {
  boardId: string;
  cardId: string;
  sourceListId: string;
  targetListId: string;
  targetPosition: number;
  orderedTargetCardIds: string[];
  companyIdValue: string;
}

interface QueuedCardMoveState {
  running: boolean;
  latest: MoveWithinBoardRequest | null;
  promise: Promise<void> | null;
}

/**
 * Short Kanban URL params:
 * - `b`  -> base64url-encoded boardId UUID
 * - `c`  -> base64url-encoded cardId UUID
 * - `cm` -> base64url-encoded commentId UUID
 *
 * Backwards compatible with legacy keys:
 * - `boardId` / `cardId` / `commentId`
 */
const URL_PARAM_KEYS = {
  boardShort: 'b',
  cardShort: 'c',
  commentShort: 'cm',
  boardLegacy: 'boardId',
  cardLegacy: 'cardId',
  commentLegacy: 'commentId',
} as const;
const COMPANY_ALIAS_PARAM_KEY = 'co';

function isUuid(value: string): boolean {
  return UUID_REGEX.test(value);
}

function uuidToBytes(uuid: string): Uint8Array {
  const hex = uuid.replace(/-/g, '').toLowerCase();
  if (hex.length !== 32) {
    throw new Error('Invalid UUID format');
  }

  const bytes = new Uint8Array(16);
  for (let i = 0; i < 16; i++) {
    bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  }
  return bytes;
}

function bytesToUuid(bytes: Uint8Array): string {
  if (bytes.length !== 16) throw new Error('Invalid UUID byte length');
  const hex = Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(
    16,
    20,
  )}-${hex.slice(20)}`;
}

function base64UrlEncode(bytes: Uint8Array): string {
  if (typeof btoa !== 'function') {
    throw new Error('btoa is not available');
  }

  let binary = '';
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });

  const b64 = btoa(binary);
  return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function base64UrlDecode(value: string): Uint8Array {
  if (typeof atob !== 'function') {
    throw new Error('atob is not available');
  }

  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  // pad to 4-char multiple
  const padded = normalized + '==='.slice((normalized.length + 3) % 4);
  const binary = atob(padded);

  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function encodeUuidForUrl(uuid: string): string {
  if (!isUuid(uuid)) {
    throw new Error('encodeUuidForUrl expects a UUID string');
  }
  return base64UrlEncode(uuidToBytes(uuid));
}

function decodeUuidFromUrl(value: string): string {
  // If it already looks like a UUID, treat it as-is.
  if (isUuid(value)) return value;
  return bytesToUuid(base64UrlDecode(value));
}

function decodeMaybeUuidParam(raw: string | null): string | null {
  if (!raw) return null;
  try {
    return decodeUuidFromUrl(raw);
  } catch {
    return null;
  }
}

function withCompanyAliasLast(params: URLSearchParams): URLSearchParams {
  if (!params.has(COMPANY_ALIAS_PARAM_KEY)) return params;

  const companyAlias = params.get(COMPANY_ALIAS_PARAM_KEY) ?? '';
  const ordered = new URLSearchParams();
  params.forEach((value, key) => {
    if (key !== COMPANY_ALIAS_PARAM_KEY) {
      ordered.append(key, value);
    }
  });
  ordered.append(COMPANY_ALIAS_PARAM_KEY, companyAlias);
  return ordered;
}

export interface UseKanbanBoardReturn {
  companyId: string | null;
  activeBoardId: string | null;
  activeBoard: ApiBoard | null;
  setActiveBoardId: (id: string | null) => void;
  boards: ApiBoard[];
  boardsLoading: boolean;
  boardsError: string | null;
  creatingBoard: boolean;
  boardDataLoading: boolean;
  boardDataError: string | null;
  loadBoardData: (boardId: string, isReload?: boolean) => Promise<void>;
  columns: KanbanColumn[];
  cards: Record<string, KanbanCardData>;
  boardLabels: KanbanLabel[];
  boardMembers: BoardMember[];
  editingBoardTitle: boolean;
  setEditingBoardTitle: (v: boolean) => void;
  boardTitleDraft: string;
  setBoardTitleDraft: (v: string) => void;
  handleBoardTitleClick: () => void;
  handleBoardTitleSubmit: () => Promise<void>;
  boardMenuButtonRef: React.RefObject<HTMLButtonElement | null>;
  boardMenuOpen: boolean;
  setBoardMenuOpen: (v: boolean | ((prev: boolean) => boolean)) => void;
  archiveCount: number;
  boardWrapperRef: React.RefObject<HTMLDivElement | null>;
  sensors: ReturnType<typeof useSensors>;
  collisionDetection: ReturnType<typeof createProximityCollisionDetection>;
  handleDragStart: (event: DragStartEvent) => void;
  handleDragOver: (event: DragOverEvent) => void;
  handleDragCancel: () => void;
  handleDragEnd: (event: DragEndEvent) => Promise<void>;
  handleBoardMouseDown: (e: React.MouseEvent) => void;
  handleBoardMouseMove: (e: React.MouseEvent) => void;
  handleBoardMouseUp: () => void;
  handleBoardMouseLeave: () => void;
  addingColumn: boolean;
  newColumnTitle: string;
  setNewColumnTitle: (v: string) => void;
  handleAddColumnClick: () => void;
  handleAddColumnConfirm: () => Promise<void>;
  handleAddColumnCancel: () => void;
  activeId: string | null;
  activeCard: KanbanCardData | null;
  activeListColumn: KanbanColumn | null;
  selectedCardId: string | null;
  setSelectedCardId: (id: string | null) => void;
  commentIdToScroll: string | null;
  onCopyCardLink: (cardId: string) => Promise<void>;
  onCopyCommentLink: (cardId: string, commentId: string) => Promise<void>;
  selectedCard: KanbanCardData | null;
  selectedColumnTitle: string;
  setRenameCardId: (id: string | null) => void;
  renameCardId: string | null;
  editingColumnId: string | null;
  setEditingColumnId: (v: string | null) => void;
  editingColumnTitle: string;
  setEditingColumnTitle: (v: string) => void;
  editingCardTitleId: string | null;
  setEditingCardTitleId: (v: string | null) => void;
  editingCardTitleValue: string;
  setEditingCardTitleValue: (v: string) => void;
  addingCardColumnId: string | null;
  newCardTitle: string;
  setNewCardTitle: (v: string) => void;
  handleColumnTitleClick: (columnId: string) => void;
  handleColumnTitleSubmit: () => Promise<void>;
  handleAddCardClick: (columnId: string) => void;
  handleAddCardConfirm: (columnId: string) => Promise<void>;
  handleAddCardCancel: () => void;
  handleOpenListMenu: (columnId: string, e: React.MouseEvent) => void;
  handleOpenCardMenu: (
    cardId: string,
    currentColumnId: string,
    e: React.MouseEvent,
  ) => void;
  handleCardClick: (cardId: string) => void;
  handleUpdateCard: (
    cardId: string,
    updates: KanbanCardPatchPayload,
  ) => Promise<void>;
  handleBoardCardTitleSubmit: () => void;
  handleBoardCardTitleCancel: () => void;
  handleAddComment: (
    cardId: string,
    text: string,
    attachments?: KanbanCardCommentAttachment[],
  ) => Promise<void>;
  handleMoveCard: (
    cardId: string,
    targetColumnId: string,
    targetBoardId?: string,
    targetPosition0Based?: number,
  ) => Promise<void>;
  getBoardListsForMove: (boardId: string) => Promise<KanbanColumn[]>;
  listMenuAnchor: { columnId: string; rect: DOMRect } | null;
  listMenuView: 'main' | 'copyList' | 'moveList' | 'moveAllCards' | 'sortBy';
  setListMenuAnchor: (v: { columnId: string; rect: DOMRect } | null) => void;
  setListMenuView: (
    v: 'main' | 'copyList' | 'moveList' | 'moveAllCards' | 'sortBy',
  ) => void;
  copyListName: string;
  setCopyListName: (v: string) => void;
  moveListPosition: number;
  setMoveListPosition: (v: number) => void;
  moveAllCardsTargetId: string;
  setMoveAllCardsTargetId: (v: string) => void;
  sortByOption:
    'titleAsc' | 'titleDesc' | 'startAsc' | 'startDesc' | 'dueAsc' | 'dueDesc';
  setSortByOption: (
    v:
      | 'titleAsc'
      | 'titleDesc'
      | 'startAsc'
      | 'startDesc'
      | 'dueAsc'
      | 'dueDesc',
  ) => void;
  handleListMenuAction: (
    columnId: string,
    action: 'addCard' | 'copyList' | 'moveList' | 'moveAllCards' | 'sortBy',
  ) => void;
  handleCopyListCreate: () => Promise<void>;
  handleMoveListToPosition: () => Promise<void>;
  handleMoveAllCardsToTarget: () => Promise<void>;
  handleSortListBy: () => Promise<void>;
  handleArchiveList: (columnId: string) => Promise<void>;
  handleArchiveListWithConfirm: (columnId: string) => void;
  handleConfirmArchiveList: () => void;
  archiveListConfirmColumnId: string | null;
  setArchiveListConfirmColumnId: (v: string | null) => void;
  handleArchiveAllCardsInList: (columnId: string) => Promise<void>;
  cardMenuAnchor: {
    cardId: string;
    currentColumnId: string;
    rect: DOMRect;
  } | null;
  cardMenuView: 'main' | 'moveToList';
  setCardMenuAnchor: (v: UseKanbanBoardReturn['cardMenuAnchor']) => void;
  setCardMenuView: (v: 'main' | 'moveToList') => void;
  handleArchiveCard: (cardId: string) => Promise<void>;
  showArchivedPanel: boolean;
  setShowArchivedPanel: (v: boolean) => void;
  showMembersPanel: boolean;
  setShowMembersPanel: (v: boolean) => void;
  archiveSearch: string;
  setArchiveSearch: (v: string) => void;
  archiveFilterType: 'all' | 'cards' | 'lists';
  setArchiveFilterType: (v: 'all' | 'cards' | 'lists') => void;
  archiveSort: 'date' | 'name' | 'type';
  setArchiveSort: (v: 'date' | 'name' | 'type') => void;
  archiveItemsGroupedByPeriod: {
    cardsInPeriod: ArchivedCard[];
    listsInPeriod: ArchivedList[];
  };
  filteredArchivedCards: ArchivedCard[];
  filteredArchivedLists: ArchivedList[];
  handleRestoreCard: (
    archived: ArchivedCard,
    targetListId?: string,
  ) => Promise<void>;
  handleRestoreCardWithTarget: (
    archived: ArchivedCard,
    targetListId: string,
  ) => void;
  handleRestoreList: (
    archived: ArchivedList,
    targetIndex?: number,
  ) => Promise<void>;
  setDeleteConfirmItem: (v: UseKanbanBoardReturn['deleteConfirmItem']) => void;
  deleteConfirmItem:
    | {
        type: 'card';
        item: ArchivedCard;
      }
    | {
        type: 'list';
        item: ArchivedList;
      }
    | null;
  handleConfirmPermanentDelete: () => Promise<void>;
  restoreCardItem: ArchivedCard | null;
  setRestoreCardItem: (v: ArchivedCard | null) => void;
  restoreTargetListId: string | null;
  setRestoreTargetListId: (v: string | null) => void;
  boardToDelete: ApiBoard | null;
  setBoardToDelete: (v: ApiBoard | null) => void;
  handleConfirmDeleteBoard: () => Promise<void>;
  addMemberEmail: string;
  setAddMemberEmail: (v: string) => void;
  addingMember: boolean;
  memberToRemove: BoardMember | null;
  setMemberToRemove: (v: BoardMember | null) => void;
  handleLinkUserToBoard: (email: string) => Promise<void>;
  handleConfirmRemoveBoardMember: () => Promise<void>;
  toast: { message: string; type: 'success' | 'error' } | null;
  setToast: (v: { message: string; type: 'success' | 'error' } | null) => void;
  createBoardForPicker: () => Promise<void>;
  createLabel: (
    boardId: string,
    label: { name: string; color: string },
    companyId: string,
  ) => Promise<{ id: string; name: string; color: string } | null>;
  updateLabel: (
    boardId: string,
    labelId: string,
    updates: { name?: string; color?: string },
    companyId: string,
  ) => Promise<{ id: string; name: string; color: string } | null>;
  deleteLabel: (
    boardId: string,
    labelId: string,
    companyId: string,
  ) => Promise<void>;
}

/**
 * After a card PATCH, the API may return comments without attachments (we strip
 * them in the request). Merge so each comment keeps its attachments from the
 * previous card state, avoiding all comment images disappearing when one comment
 * is edited or deleted.
 */
function mergeCardCommentsPreservingAttachments(
  updated: KanbanCardData,
  prevCard: KanbanCardData | undefined,
): KanbanCardData {
  if (!updated.comments?.length) return updated;
  if (!prevCard?.comments?.length) return updated;
  const mergedComments = updated.comments.map((c) => {
    const prevComment = prevCard.comments?.find((p) => p.id === c.id);
    const attachments =
      (c.attachments?.length ? c.attachments : undefined) ??
      prevComment?.attachments;
    return attachments?.length ? { ...c, attachments } : c;
  });
  const merged = { ...updated, comments: mergedComments };
  return deduplicateCommentsWithSameAttachments(merged);
}

/**
 * Deduplicate comments when the backend returns two for one user action:
 * - One with image only and one with image + text: keep the one with text.
 * - Two image-only comments with same attachments: keep one, remove duplicate.
 */
function deduplicateCommentsWithSameAttachments(
  card: KanbanCardData,
): KanbanCardData {
  const comments = card.comments;
  if (!comments?.length) return card;
  const pathSet = (c: KanbanCardComment) =>
    new Set((c.attachments ?? []).map((a) => a.path));
  const hasMeaningfulText = (c: KanbanCardComment) =>
    (c.text?.trim() ?? '') && c.text !== '\u00A0';
  const toRemove = new Set<string>();
  for (let i = 0; i < comments.length; i++) {
    for (let j = i + 1; j < comments.length; j++) {
      const a = comments[i];
      const b = comments[j];
      const pathsA = pathSet(a);
      const pathsB = pathSet(b);
      if (pathsA.size === 0 || pathsB.size === 0) continue;
      const samePaths =
        pathsA.size === pathsB.size &&
        Array.from(pathsA).every((p) => pathsB.has(p));
      if (!samePaths) continue;
      const aHasText = hasMeaningfulText(a);
      const bHasText = hasMeaningfulText(b);
      const aHasAuthor = Boolean(a.authorName?.trim());
      const bHasAuthor = Boolean(b.authorName?.trim());
      if (aHasText && !bHasText) toRemove.add(b.id);
      else if (!aHasText && bHasText) toRemove.add(a.id);
      else if (!aHasText && !bHasText) {
        if (bHasAuthor && !aHasAuthor) toRemove.add(a.id);
        else toRemove.add(b.id);
      }
    }
  }
  if (toRemove.size === 0) return card;
  const filtered = comments.filter((c) => !toRemove.has(c.id));
  return { ...card, comments: filtered };
}

function appendLocalActivityIfMissing(
  card: KanbanCardData,
  text: string,
  actorName: string,
  actorId?: string,
): KanbanCardData {
  const normalized = text.trim();
  if (!normalized) return card;
  const activity: KanbanCardComment = {
    id: `local-activity-${generateId()}`,
    authorName: actorName,
    ...(actorId ? { authorId: actorId } : {}),
    kind: 'activity',
    text: normalized,
    createdAt: new Date().toISOString(),
  };
  return { ...card, comments: [...(card.comments ?? []), activity] };
}

function hasActivityText(card: KanbanCardData, text: string): boolean {
  const normalized = text.trim().toLowerCase();
  if (!normalized) return false;
  return (card.comments ?? []).some(
    (comment) =>
      comment.kind === 'activity' &&
      (comment.text ?? '').trim().toLowerCase() === normalized,
  );
}

export function useKanbanBoard(): UseKanbanBoardReturn {
  const [searchParams, setSearchParams] = useSearchParams();
  const companyId = useActiveCompanyStore((s) => s.activeCompanyId);
  const profile = useUserProfileStore((s) => s.profile);
  const profileId = profile?.id ?? null;
  const activeBoardIdFromUrl = decodeMaybeUuidParam(
    searchParams.get(URL_PARAM_KEYS.boardShort) ??
      searchParams.get(URL_PARAM_KEYS.boardLegacy),
  );
  const activeBoardId = activeBoardIdFromUrl || null;
  const urlCardId = decodeMaybeUuidParam(
    searchParams.get(URL_PARAM_KEYS.cardShort) ??
      searchParams.get(URL_PARAM_KEYS.cardLegacy),
  );
  const urlCommentId = decodeMaybeUuidParam(
    searchParams.get(URL_PARAM_KEYS.commentShort) ??
      searchParams.get(URL_PARAM_KEYS.commentLegacy),
  );

  const [boards, setBoards] = useState<ApiBoard[]>([]);
  const [boardsLoading, setBoardsLoading] = useState(false);
  const [boardsError, setBoardsError] = useState<string | null>(null);
  // Used to avoid redirecting before we have the latest allowed board list.
  const [hasFetchedBoards, setHasFetchedBoards] = useState(false);
  const [boardDataLoading, setBoardDataLoading] = useState(false);
  const [boardDataError, setBoardDataError] = useState<string | null>(null);
  const [editingBoardTitle, setEditingBoardTitle] = useState(false);
  const [boardTitleDraft, setBoardTitleDraft] = useState('');
  const [columns, setColumns] = useState<KanbanColumn[]>([]);
  const columnsRef = useRef<KanbanColumn[]>([]);
  useEffect(() => {
    columnsRef.current = columns;
  }, [columns]);
  const [cards, setCards] = useState<Record<string, KanbanCardData>>({});
  const [boardLabels, setBoardLabels] = useState<KanbanLabel[]>([]);
  const [boardMembers, setBoardMembers] = useState<BoardMember[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [renameCardId, setRenameCardId] = useState<string | null>(null);
  const [editingCardTitleId, setEditingCardTitleId] = useState<string | null>(
    null,
  );
  const [editingCardTitleValue, setEditingCardTitleValue] = useState('');
  const [editingColumnId, setEditingColumnId] = useState<string | null>(null);
  const [editingColumnTitle, setEditingColumnTitle] = useState('');
  const [addingColumn, setAddingColumn] = useState(false);
  const [newColumnTitle, setNewColumnTitle] = useState('');
  const [creatingBoard, setCreatingBoard] = useState(false);
  const [addingCardColumnId, setAddingCardColumnId] = useState<string | null>(
    null,
  );
  const [newCardTitle, setNewCardTitle] = useState('');
  const [listMenuAnchor, setListMenuAnchor] = useState<{
    columnId: string;
    rect: DOMRect;
  } | null>(null);
  const [listMenuView, setListMenuView] = useState<
    'main' | 'copyList' | 'moveList' | 'moveAllCards' | 'sortBy'
  >('main');
  const [cardMenuAnchor, setCardMenuAnchor] = useState<{
    cardId: string;
    currentColumnId: string;
    rect: DOMRect;
  } | null>(null);
  const [cardMenuView, setCardMenuView] = useState<'main' | 'moveToList'>(
    'main',
  );
  const [copyListName, setCopyListName] = useState('');
  const [moveListPosition, setMoveListPosition] = useState(0);
  const [moveAllCardsTargetId, setMoveAllCardsTargetId] = useState('');
  const [sortByOption, setSortByOption] = useState<
    'titleAsc' | 'titleDesc' | 'startAsc' | 'startDesc' | 'dueAsc' | 'dueDesc'
  >('titleAsc');
  const [archivedCards, setArchivedCards] = useState<ArchivedCard[]>([]);
  const [archivedLists, setArchivedLists] = useState<ArchivedList[]>([]);
  const [showArchivedPanel, setShowArchivedPanel] = useState(false);
  const [showMembersPanel, setShowMembersPanel] = useState(false);
  const [boardMenuOpen, setBoardMenuOpen] = useState(false);
  const [addMemberEmail, setAddMemberEmail] = useState('');
  const [addingMember, setAddingMember] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<BoardMember | null>(
    null,
  );
  const [archiveSearch, setArchiveSearch] = useState('');
  const [archiveFilterType, setArchiveFilterType] = useState<
    'all' | 'cards' | 'lists'
  >('all');
  const [archiveSort, setArchiveSort] = useState<'date' | 'name' | 'type'>(
    'date',
  );
  const [restoreTargetListId, setRestoreTargetListId] = useState<string | null>(
    null,
  );
  const [restoreCardItem, setRestoreCardItem] = useState<ArchivedCard | null>(
    null,
  );
  const [deleteConfirmItem, setDeleteConfirmItem] = useState<
    | { type: 'card'; item: ArchivedCard }
    | { type: 'list'; item: ArchivedList }
    | null
  >(null);
  const [boardToDelete, setBoardToDelete] = useState<ApiBoard | null>(null);
  const [archiveListConfirmColumnId, setArchiveListConfirmColumnId] = useState<
    string | null
  >(null);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  const companyService = useCompanyServiceQueries();
  const hasDraggedRef = useRef(false);
  // For touch/mouse drops where `event.over` can be `null` or resolve to a list
  // container instead of a card, we still want to commit based on the last
  // collided card and the preview state.
  const lastOverCardIdRef = useRef<string | null>(null);
  const lastOverColumnIdRef = useRef<string | null>(null);
  const lastOverIsBelowRef = useRef(false);
  const dragStartColumnIdRef = useRef<string | null>(null);
  const dragStartCardIdsRef = useRef<string[] | null>(null);
  const isTouchCardDragRef = useRef(false);
  const dragPreviewColumnRef = useRef<string | null>(null);
  const boardScrollResetForBoardIdRef = useRef<string | null>(null);
  const boardWrapperRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef({ isDown: false, startPageX: 0, scrollLeft: 0 });
  const boardMenuButtonRef = useRef<HTMLButtonElement>(null);
  const queuedCardMovesRef = useRef<Map<string, QueuedCardMoveState>>(
    new Map(),
  );
  const searchParamsBeforeCardModalRef = useRef<string | null>(null);

  const showToast = useCallback(
    (message: string, type: 'success' | 'error') => {
      setToast({ message, type });
    },
    [],
  );
  // Prevent duplicate REST calls when the UI triggers "create label" twice
  // (common with optimistic UI + socket events).
  const labelCreateInFlightRef = useRef<
    Map<string, Promise<{ id: string; name: string; color: string } | null>>
  >(new Map());
  const labelCreatedCacheRef = useRef<
    Map<string, { id: string; name: string; color: string }>
  >(new Map());

  const setActiveBoardId = useCallback(
    (boardId: string | null) => {
      if (boardId) {
        setSearchParams(
          withCompanyAliasLast(
            new URLSearchParams({
              [URL_PARAM_KEYS.boardShort]: encodeUuidForUrl(boardId),
            }),
          ),
        );
      } else {
        setSearchParams({});
      }
    },
    [setSearchParams],
  );

  const lastProfileIdRef = useRef<string | null>(null);
  useEffect(() => {
    const prevProfileId = lastProfileIdRef.current;
    // Only reset when the authenticated user actually changes (not on first load).
    if (prevProfileId != null && prevProfileId !== profileId) {
      setHasFetchedBoards(false);
      setBoards([]);
      setBoardsLoading(false);
      setBoardsError(null);
      setBoardDataLoading(false);
      setBoardDataError(null);
      setColumns([]);
      setCards({});
      setBoardLabels([]);
      setBoardMembers([]);
      setArchivedCards([]);
      setArchivedLists([]);

      setSelectedCardId(null);
      setRenameCardId(null);
      setEditingBoardTitle(false);

      setSearchParams({});
      void clearKanbanOfflineCache();
    }

    lastProfileIdRef.current = profileId;
  }, [profileId, setSearchParams]);

  const loadBoards = useCallback(async () => {
    if (!companyId) return;
    setHasFetchedBoards(false);
    setBoardsLoading(true);
    setBoardsError(null);
    try {
      const list = await fetchBoards(companyId);
      setBoards(list ?? []);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load boards';
      const isForbidden =
        /403|forbidden|not authorized|unauthorized|not allowed/i.test(msg);

      if (isForbidden) {
        // Board list should fail closed; never fall back to company-wide boards.
        setBoardsError(null);
        setBoards([]);
        showToast('No boards available', 'error');
      } else {
        setBoardsError(msg);
        setBoards([]);
      }
    } finally {
      setBoardsLoading(false);
      setHasFetchedBoards(true);
    }
  }, [companyId, showToast]);

  const loadBoardData = useCallback(
    async (boardId: string, isReload = false) => {
      if (!companyId) return;
      if (!isReload) setBoardDataLoading(true);
      setBoardDataError(null);
      try {
        const [listsRes, cardsRes, labelsRes, membersRes, archiveRes] =
          await Promise.all([
            fetchLists(boardId, companyId),
            fetchCards(boardId, companyId),
            fetchLabels(boardId, companyId),
            fetchMembers(boardId, companyId),
            fetchArchive(boardId, companyId),
          ]);
        setColumns(buildColumnsFromListsAndCards(listsRes, cardsRes));
        setCards(
          Object.fromEntries(
            Object.entries(apiCardsToRecord(cardsRes)).map(([id, card]) => [
              id,
              deduplicateCommentsWithSameAttachments(card),
            ]),
          ),
        );
        setBoardLabels(labelsRes ?? []);
        setBoardMembers(membersRes ?? []);
        const arch = archiveRes;
        const normalizedCards: ArchivedCard[] = (arch.archivedCards ?? []).map(
          (a) => ({
            id: a.id,
            archivedAt: a.archivedAt,
            card: a.card,
            originalListId: a.originalListId,
            originalListTitle: a.originalListTitle ?? '',
            originalIndex: a.originalPosition ?? 0,
          }),
        );
        const normalizedLists: ArchivedList[] = (arch.archivedLists ?? []).map(
          (a) => ({
            id: a.id,
            archivedAt: a.archivedAt,
            column: a.column,
            originalIndex: a.originalPosition ?? 0,
            cardsSnapshot: a.cardsSnapshot ?? {},
          }),
        );
        setArchivedCards(normalizedCards);
        setArchivedLists(normalizedLists);
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : 'Failed to load board data';
        const isBoardNotInCompany =
          /does not belong|not belong to this company|forbidden|403/i.test(msg);
        if (isBoardNotInCompany) {
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete(URL_PARAM_KEYS.boardShort);
            next.delete(URL_PARAM_KEYS.cardShort);
            next.delete(URL_PARAM_KEYS.commentShort);
            next.delete(URL_PARAM_KEYS.boardLegacy);
            next.delete(URL_PARAM_KEYS.cardLegacy);
            next.delete(URL_PARAM_KEYS.commentLegacy);
            return withCompanyAliasLast(next);
          });
          showToast("You don't have access to this board", 'error');

          // Fail closed: clear any previously-rendered board data immediately.
          setColumns([]);
          setCards({});
          setBoardLabels([]);
          setBoardMembers([]);
          setArchivedCards([]);
          setArchivedLists([]);

          setBoardDataError(null);
        } else {
          setBoardDataError(msg);
        }
        // Optimistic UX: keep the last-rendered board data on screen.
        // Users can retry without losing context.
      } finally {
        setBoardDataLoading(false);
      }
    },
    [companyId, setSearchParams, showToast],
  );

  useEffect(() => {
    if (companyId) void loadBoards();
  }, [companyId, profileId, loadBoards]);

  const isActiveBoardAllowed = useMemo(
    () => (activeBoardId ? boards.some((b) => b.id === activeBoardId) : false),
    [activeBoardId, boards],
  );

  useEffect(() => {
    if (activeBoardId && companyId) {
      if (hasFetchedBoards && !isActiveBoardAllowed) {
        showToast("You don't have access to this board", 'error');
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete(URL_PARAM_KEYS.boardShort);
          next.delete(URL_PARAM_KEYS.cardShort);
          next.delete(URL_PARAM_KEYS.commentShort);
          next.delete(URL_PARAM_KEYS.boardLegacy);
          next.delete(URL_PARAM_KEYS.cardLegacy);
          next.delete(URL_PARAM_KEYS.commentLegacy);
          return withCompanyAliasLast(next);
        });

        // Fail closed: clear any previously-rendered board data.
        setColumns([]);
        setCards({});
        setBoardLabels([]);
        setBoardMembers([]);
        setArchivedCards([]);
        setArchivedLists([]);
        return;
      }

      void loadBoardData(activeBoardId);
    } else {
      setColumns([]);
      setCards({});
      setBoardLabels([]);
      setBoardMembers([]);
      setArchivedCards([]);
      setArchivedLists([]);
    }
  }, [
    activeBoardId,
    companyId,
    loadBoardData,
    hasFetchedBoards,
    isActiveBoardAllowed,
    showToast,
    setSearchParams,
  ]);

  useEffect(() => {
    if (!activeBoardId) {
      boardScrollResetForBoardIdRef.current = null;
      return;
    }

    const alreadyResetForCurrentBoard =
      boardScrollResetForBoardIdRef.current === activeBoardId;
    if (alreadyResetForCurrentBoard) {
      return;
    }

    const timeoutIds: number[] = [];
    let tickCount = 0;
    const maxTicks = 50; // ~5s to wait for late mount/render
    const intervalId = window.setInterval(() => {
      const boardEl = boardWrapperRef.current;
      if (boardEl) {
        // Temporarily disable smooth behavior so reset is immediate and not
        // affected by snap animation settling on later lists.
        const previousScrollBehavior = boardEl.style.scrollBehavior;
        boardEl.style.scrollBehavior = 'auto';
        boardEl.scrollLeft = 0;
        timeoutIds.push(
          window.setTimeout(() => {
            const el = boardWrapperRef.current;
            if (el) {
              el.scrollLeft = 0;
            }
          }, 120),
        );
        timeoutIds.push(
          window.setTimeout(() => {
            const el = boardWrapperRef.current;
            if (el) {
              el.scrollLeft = 0;
              el.style.scrollBehavior = previousScrollBehavior;
            }
          }, 300),
        );
        // Only mark as done once board data finished loading. This ensures
        // slow connections still get a post-load reset to the first list.
        if (!boardDataLoading) {
          boardScrollResetForBoardIdRef.current = activeBoardId;
        }
        window.clearInterval(intervalId);
        return;
      }

      tickCount += 1;
      if (tickCount >= maxTicks) {
        window.clearInterval(intervalId);
      }
    }, 100);

    return () => {
      window.clearInterval(intervalId);
      timeoutIds.forEach((id) => window.clearTimeout(id));
    };
  }, [activeBoardId, boardDataLoading, columns.length]);

  const setSelectedCardIdWrapped = useCallback(
    (id: string | null) => {
      setSelectedCardId(id);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (!id) {
          const previousSearchParams = searchParamsBeforeCardModalRef.current;
          searchParamsBeforeCardModalRef.current = null;
          if (previousSearchParams != null) {
            return withCompanyAliasLast(
              new URLSearchParams(previousSearchParams),
            );
          }
          next.delete(URL_PARAM_KEYS.cardShort);
          next.delete(URL_PARAM_KEYS.commentShort);
          next.delete(URL_PARAM_KEYS.cardLegacy);
          next.delete(URL_PARAM_KEYS.commentLegacy);
          return withCompanyAliasLast(next);
        }
        searchParamsBeforeCardModalRef.current = next.toString();
        next.set(URL_PARAM_KEYS.cardShort, encodeUuidForUrl(id));
        next.delete(URL_PARAM_KEYS.commentShort);
        next.delete(URL_PARAM_KEYS.commentLegacy);
        // remove legacy card param once we switch to short format
        next.delete(URL_PARAM_KEYS.cardLegacy);
        return withCompanyAliasLast(next);
      });
    },
    [setSearchParams],
  );

  useEffect(() => {
    if (!activeBoardId) return;

    const encodedBoardId = encodeUuidForUrl(activeBoardId);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        const hasCardParam =
          Boolean(next.get(URL_PARAM_KEYS.cardShort)) ||
          Boolean(next.get(URL_PARAM_KEYS.cardLegacy));

        if (hasCardParam) {
          const hasCorrectBoardShort =
            next.get(URL_PARAM_KEYS.boardShort) === encodedBoardId;
          const hasLegacyBoard = next.has(URL_PARAM_KEYS.boardLegacy);
          if (hasCorrectBoardShort && !hasLegacyBoard) return prev;
          next.set(URL_PARAM_KEYS.boardShort, encodedBoardId);
          next.delete(URL_PARAM_KEYS.boardLegacy);
          return withCompanyAliasLast(next);
        }

        const hasCorrectBoardShort =
          next.get(URL_PARAM_KEYS.boardShort) === encodedBoardId;
        const hasLegacyBoard = next.has(URL_PARAM_KEYS.boardLegacy);
        if (hasCorrectBoardShort && !hasLegacyBoard) return prev;

        next.set(URL_PARAM_KEYS.boardShort, encodedBoardId);
        next.delete(URL_PARAM_KEYS.boardLegacy);
        return withCompanyAliasLast(next);
      },
      { replace: true },
    );
  }, [activeBoardId, searchParams, setSearchParams]);

  useEffect(() => {
    if (urlCardId && activeBoardId && cards[urlCardId]) {
      setSelectedCardId(urlCardId);
    }
  }, [urlCardId, activeBoardId, cards]);

  const onCopyCommentLink = useCallback(
    async (cardId: string, commentId: string): Promise<void> => {
      const params = new URLSearchParams({
        [URL_PARAM_KEYS.cardShort]: encodeUuidForUrl(cardId),
        [URL_PARAM_KEYS.commentShort]: encodeUuidForUrl(commentId),
      });
      if (activeBoardId) {
        params.set(URL_PARAM_KEYS.boardShort, encodeUuidForUrl(activeBoardId));
      }
      const url = `${window.location.origin}${ROUTES.KANBAN}?${params.toString()}`;

      if (
        navigator.clipboard &&
        typeof navigator.clipboard.writeText === 'function'
      ) {
        await navigator.clipboard.writeText(url);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = url;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        const copied = document.execCommand('copy');
        document.body.removeChild(textarea);
        if (!copied) {
          throw new Error('Failed to copy comment link.');
        }
      }
      showToast('Comment link copied.', 'success');
    },
    [activeBoardId, showToast],
  );

  const onCopyCardLink = useCallback(
    async (cardId: string): Promise<void> => {
      const params = new URLSearchParams();
      if (activeBoardId) {
        params.set(URL_PARAM_KEYS.boardShort, encodeUuidForUrl(activeBoardId));
      }
      params.set(URL_PARAM_KEYS.cardShort, encodeUuidForUrl(cardId));
      const orderedParams = withCompanyAliasLast(params);
      const url = `${window.location.origin}${ROUTES.KANBAN}?${orderedParams.toString()}`;

      if (
        navigator.clipboard &&
        typeof navigator.clipboard.writeText === 'function'
      ) {
        await navigator.clipboard.writeText(url);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = url;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        const copied = document.execCommand('copy');
        document.body.removeChild(textarea);
        if (!copied) {
          throw new Error('Failed to copy card link.');
        }
      }
      showToast('Card link copied.', 'success');
    },
    [activeBoardId, showToast],
  );

  const allowedBoardIds = useMemo(
    () => new Set(boards.map((b) => b.id)),
    [boards],
  );
  const socketBoardId = useMemo(
    () => (hasFetchedBoards && isActiveBoardAllowed ? activeBoardId : null),
    [hasFetchedBoards, isActiveBoardAllowed, activeBoardId],
  );

  useKanbanSocket(socketBoardId, companyId, {
    onBoardUpdated: (board) => {
      if (!allowedBoardIds.has(board.id)) return;
      setBoards((prev) =>
        prev.map((b) => (b.id === board.id ? { ...b, title: board.title } : b)),
      );
    },
    onBoardDeleted: (payload) => {
      if (!allowedBoardIds.has(payload.boardId)) return;
      setBoards((prev) => prev.filter((b) => b.id !== payload.boardId));
      if (payload.boardId === activeBoardId) {
        setSearchParams({});
        setColumns([]);
        setCards({});
        setBoardLabels([]);
        setBoardMembers([]);
        setArchivedCards([]);
        setArchivedLists([]);
      }
    },
    onListCreated: (list) => {
      setColumns((prev) => {
        if (prev.some((c) => c.id === list.id)) return prev;
        const col = listPayloadToColumn({ ...list, cardIds: [] });
        const next = [...prev];
        next.splice(list.position, 0, col);
        return next;
      });
    },
    onListUpdated: (list) => {
      setColumns((prev) =>
        prev.map((c) => (c.id === list.id ? { ...c, title: list.title } : c)),
      );
    },
    onListReordered: (payload) => {
      setColumns((prev) => {
        const byId = new Map(prev.map((c) => [c.id, c]));
        const ordered = payload.listIds
          .map((id) => byId.get(id))
          .filter(Boolean) as KanbanColumn[];
        const rest = prev.filter((c) => !payload.listIds.includes(c.id));
        return ordered.length > 0 ? [...ordered, ...rest] : prev;
      });
    },
    onListDeleted: (payload) => {
      setColumns((prev) => {
        const col = prev.find((c) => c.id === payload.listId);
        const removedIds = col?.cardIds ?? [];
        setCards((cardPrev) => {
          const next = { ...cardPrev };
          removedIds.forEach((id) => delete next[id]);
          return next;
        });
        return prev.filter((c) => c.id !== payload.listId);
      });
    },
    onCardCreated: (card) => {
      const listId = card.listId;
      if (!listId) return;
      setCards((prev) => ({ ...prev, [card.id]: card }));
      setColumns((prev) =>
        prev.map((c) =>
          c.id === listId
            ? {
                ...c,
                cardIds: c.cardIds.includes(card.id)
                  ? c.cardIds
                  : [...c.cardIds, card.id],
              }
            : c,
        ),
      );
    },
    onCardUpdated: (card) => {
      setCards((prev) => {
        const merged = mergeCardCommentsPreservingAttachments(
          card,
          prev[card.id],
        );
        return { ...prev, [card.id]: merged };
      });
      const listId = card.listId;
      if (listId) {
        setColumns((prev) =>
          prev.map((c) =>
            c.id === listId
              ? {
                  ...c,
                  cardIds: c.cardIds.includes(card.id)
                    ? c.cardIds
                    : [...c.cardIds, card.id],
                }
              : { ...c, cardIds: c.cardIds.filter((id) => id !== card.id) },
          ),
        );
      }
    },
    onCardReordered: (payload) => {
      const reorderedIds = new Set(payload.cardIds);
      setColumns((prev) =>
        prev.map((c) =>
          c.id === payload.listId
            ? { ...c, cardIds: payload.cardIds }
            : {
                ...c,
                cardIds: c.cardIds.filter((id) => !reorderedIds.has(id)),
              },
        ),
      );
    },
    onCardComment: (card) => {
      setCards((prev) => {
        const merged = mergeCardCommentsPreservingAttachments(
          card,
          prev[card.id],
        );
        return { ...prev, [card.id]: merged };
      });
    },
    onCardArchived: (payload) => {
      setArchivedCards((prev) => {
        if (prev.some((a) => a.id === payload.id)) return prev;
        return [
          ...prev,
          {
            id: payload.id,
            archivedAt: payload.archivedAt ?? '',
            card: payload.card,
            originalListId: payload.originalListId,
            originalListTitle: payload.originalListTitle ?? '',
            originalIndex: payload.originalPosition ?? 0,
          },
        ];
      });
      setCards((prev) => {
        const next = { ...prev };
        delete next[payload.card.id];
        return next;
      });
      setColumns((prev) =>
        prev.map((c) =>
          c.id === payload.originalListId
            ? {
                ...c,
                cardIds: c.cardIds.filter((id) => id !== payload.card.id),
              }
            : c,
        ),
      );
    },
    onListArchived: (payload) => {
      setColumns((prev) => {
        const col = prev.find((c) => c.id === payload.originalListId);
        const removedIds = col?.cardIds ?? [];
        setCards((cardPrev) => {
          const next = { ...cardPrev };
          removedIds.forEach((id) => delete next[id]);
          return next;
        });
        return prev.filter((c) => c.id !== payload.originalListId);
      });
      if (activeBoardId) void loadBoardData(activeBoardId, true);
    },
    onCardDeleted: (payload) => {
      setCards((prev) => {
        const card = prev[payload.cardId];
        if (!card) return prev;
        const listId = card.listId;
        setColumns((colPrev) =>
          colPrev.map((c) =>
            c.id === listId
              ? {
                  ...c,
                  cardIds: c.cardIds.filter((id) => id !== payload.cardId),
                }
              : c,
          ),
        );
        const next = { ...prev };
        delete next[payload.cardId];
        return next;
      });
    },
    onLabelCreated: (label) => {
      setBoardLabels((prev) =>
        prev.some((l) => l.id === label.id)
          ? prev
          : [...prev, { id: label.id, name: label.name, color: label.color }],
      );
    },
    onLabelUpdated: (label) => {
      setBoardLabels((prev) =>
        prev.map((l) =>
          l.id === label.id
            ? { id: label.id, name: label.name, color: label.color }
            : l,
        ),
      );
    },
    onLabelDeleted: (payload) => {
      setBoardLabels((prev) => prev.filter((l) => l.id !== payload.labelId));
    },
    onMemberAdded: (member) => {
      if (activeBoardId && member.boardId !== activeBoardId) return;
      setBoardMembers((prev) => {
        const existsIndex = prev.findIndex((m) => {
          const memberAny = member as { userId?: string; email?: string };
          if (m.userId && memberAny.userId)
            return m.userId === memberAny.userId;
          if (m.email && memberAny.email) {
            return m.email.toLowerCase() === memberAny.email.toLowerCase();
          }
          return m.id === member.id;
        });
        if (existsIndex !== -1) {
          const next = [...prev];
          next[existsIndex] = { ...next[existsIndex], ...member };
          return next;
        }
        return [...prev, member];
      });
    },
    onMemberRemoved: (payload) => {
      setBoardMembers((prev) => prev.filter((m) => m.id !== payload.memberId));
    },
    onArchiveCardRestored: (card) => {
      const listId = card.listId;
      if (!listId) return;
      setCards((prev) => ({ ...prev, [card.id]: card }));
      setColumns((prev) =>
        prev.map((c) =>
          c.id === listId
            ? {
                ...c,
                cardIds: c.cardIds.includes(card.id)
                  ? c.cardIds
                  : [...c.cardIds, card.id],
              }
            : c,
        ),
      );
      setArchivedCards((prev) => prev.filter((a) => a.card.id !== card.id));
    },
    onArchiveListRestored: (list) => {
      setColumns((prev) => {
        if (prev.some((c) => c.id === list.id)) return prev;
        const col = listPayloadToColumn({ ...list, cardIds: [] });
        const next = [...prev];
        next.splice(list.position, 0, col);
        return next;
      });
      setArchivedLists((prev) => prev.filter((a) => a.column.id !== list.id));
    },
    onArchiveCardDeleted: (payload) => {
      setArchivedCards((prev) =>
        prev.filter((a) => a.id !== payload.archivedCardId),
      );
    },
    onArchiveListDeleted: (payload) => {
      setArchivedLists((prev) =>
        prev.filter((a) => a.id !== payload.archivedListId),
      );
    },
    onError: (payload) => {
      const message = payload.message?.trim() ?? 'Kanban realtime error';
      // WS auth can fail transiently (cookie/session race) while REST still works.
      // Avoid noisy toasts for this case and keep Kanban usable.
      if (/not authenticated/i.test(message)) {
        return;
      }
      showToast(message, 'error');
    },
  });

  const activeBoard = useMemo(
    () =>
      activeBoardId
        ? (boards.find((b) => b.id === activeBoardId) ?? null)
        : null,
    [activeBoardId, boards],
  );

  const handleBoardTitleClick = useCallback(() => {
    if (activeBoard) {
      setEditingBoardTitle(true);
      setBoardTitleDraft(activeBoard.title);
    }
  }, [activeBoard]);

  const handleBoardTitleSubmit = useCallback(async () => {
    const trimmed = boardTitleDraft.trim();
    if (!activeBoardId || !companyId || !trimmed) {
      setEditingBoardTitle(false);
      setBoardTitleDraft('');
      return;
    }
    if (trimmed === activeBoard?.title) {
      setEditingBoardTitle(false);
      setBoardTitleDraft('');
      return;
    }
    const previousTitle = activeBoard?.title ?? trimmed;

    // Optimistically update title.
    setBoards((prev) =>
      prev.map((b) => (b.id === activeBoardId ? { ...b, title: trimmed } : b)),
    );
    // Close the editor immediately for a fully optimistic UX.
    setEditingBoardTitle(false);
    setBoardTitleDraft('');
    try {
      await updateBoard(activeBoardId, { title: trimmed }, companyId);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to rename board';
      showToast(msg, 'error');
      setBoards((prev) =>
        prev.map((b) =>
          b.id === activeBoardId ? { ...b, title: previousTitle } : b,
        ),
      );
    }
  }, [
    activeBoardId,
    activeBoard?.title,
    boardTitleDraft,
    companyId,
    showToast,
  ]);

  // Mouse: immediate drag. Touch: short delay so vertical list scroll stays 1:1 with the finger
  // (PointerSensor would capture touch and fight native scrolling on cards).
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 6 },
    }),
  );

  const collisionDetection = useMemo(
    () =>
      createProximityCollisionDetection(
        columns.map((c) => c.id),
        () => isTouchCardDragRef.current,
      ),
    [columns],
  );

  useEffect(() => {
    if (activeId) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [activeId]);

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const activeIdStr = String(event.active.id);
      setActiveId(activeIdStr);
      hasDraggedRef.current = true;

      lastOverCardIdRef.current = null;
      lastOverColumnIdRef.current = null;
      lastOverIsBelowRef.current = false;
      dragStartColumnIdRef.current = null;
      dragStartCardIdsRef.current = null;

      // Capture the starting list + order so we can persist exactly the
      // preview state on drop (even if `event.over` is null).
      const isListDrag = columns.some((c) => c.id === activeIdStr);
      isTouchCardDragRef.current =
        !isListDrag && isTouchDragActivator(event.activatorEvent);
      if (!isListDrag) {
        const startColumnId = findColumnId(columns, activeIdStr);
        dragStartColumnIdRef.current = startColumnId;
        dragPreviewColumnRef.current = startColumnId;
        dragStartCardIdsRef.current = startColumnId
          ? (columns.find((c) => c.id === startColumnId)?.cardIds.slice() ??
            null)
          : null;
      }
    },
    [columns, setActiveId],
  );

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const overId = event.over?.id;
      const activeIdStr = String(event.active.id);
      if (overId == null) return;
      const overIdStr = String(overId);
      const isListDrag = columns.some((c) => c.id === activeIdStr);
      const overIsList = columns.some((c) => c.id === overIdStr);
      if (isListDrag && overIsList && activeIdStr !== overIdStr) {
        setColumns((prev) => {
          const oldIndex = prev.findIndex((c) => c.id === activeIdStr);
          const newIndex = prev.findIndex((c) => c.id === overIdStr);
          if (oldIndex === -1 || newIndex === -1) return prev;
          const next = arrayMove(prev, oldIndex, newIndex);
          columnsRef.current = next;
          return next;
        });
        return;
      }
      const activeColumnId = findColumnId(columns, activeIdStr);
      const overColumnId = overIsList
        ? overIdStr
        : findColumnId(columns, overIdStr);
      if (!activeColumnId || !overColumnId) return;

      const previewColumnId =
        findColumnId(columns, activeIdStr) ??
        dragPreviewColumnRef.current ??
        activeColumnId;
      let effectiveOverColumnId = overColumnId;

      if (isTouchCardDragRef.current && overColumnId !== previewColumnId) {
        const activeRect = event.active.rect.current.translated;
        const overRect = event.over?.rect;
        const dragCenterX = activeRect
          ? activeRect.left + activeRect.width / 2
          : null;
        const clearlyInOverColumn =
          dragCenterX != null &&
          overRect != null &&
          isDragCenterClearlyInColumn(
            dragCenterX,
            overRect,
            getColumnEdgeRole(
              overColumnId,
              columns.map((c) => c.id),
            ),
          );

        if (clearlyInOverColumn) {
          dragPreviewColumnRef.current = overColumnId;
          effectiveOverColumnId = overColumnId;
        } else {
          effectiveOverColumnId = previewColumnId;
          if (overIsList && overIdStr !== previewColumnId) return;
          if (!overIsList && overColumnId !== previewColumnId) return;
        }
      } else {
        dragPreviewColumnRef.current = overColumnId;
      }

      // Remember the last collided card so we can persist to the "closest"
      // position even when drop happens between cards.
      if (!overIsList) {
        lastOverCardIdRef.current = overIdStr;
        lastOverColumnIdRef.current = effectiveOverColumnId;
      } else {
        // When hovering the list container (including the stretched/invisible
        // "under the list" area), we still need to remember the target column
        // so `handleDragEnd` moves the card to the correct list.
        lastOverCardIdRef.current = null;
        lastOverColumnIdRef.current = effectiveOverColumnId;
      }
      const activeRect = event.active.rect.current.translated;
      const overRect = event.over?.rect;
      const isBelowOverCard =
        activeRect != null &&
        overRect != null &&
        activeRect.top + activeRect.height / 2 >
          overRect.top + overRect.height / 2;

      // Finger/pointer is not always centered over the card on touch.
      // Using drag direction makes "insert after vs before" deterministic.
      const deltaY = event.delta.y;
      const shouldInsertAfterOverCard =
        Math.abs(deltaY) > 0.5 ? deltaY > 0 : isBelowOverCard;

      if (!overIsList) {
        lastOverIsBelowRef.current = shouldInsertAfterOverCard;
      }
      setColumns((prev) => {
        const activeCol = prev.find((c) => c.id === activeColumnId);
        const overCol = prev.find((c) => c.id === effectiveOverColumnId);
        if (!activeCol || !overCol) return prev;

        const sameList = activeColumnId === effectiveOverColumnId;
        if (sameList) {
          const activeIndex = activeCol.cardIds.indexOf(activeIdStr);
          const overIndex = overCol.cardIds.indexOf(overIdStr);
          const ids = activeCol.cardIds.filter((id) => id !== activeIdStr);

          const rawTargetIndex =
            overIndex >= 0
              ? overIndex + (shouldInsertAfterOverCard ? 1 : 0)
              : activeIndex;
          const adjustedTargetIndex =
            activeIndex < rawTargetIndex ? rawTargetIndex - 1 : rawTargetIndex;

          const insertIndex = Math.max(
            0,
            Math.min(adjustedTargetIndex, ids.length),
          );
          ids.splice(insertIndex, 0, activeIdStr);

          const next = prev.map((c) =>
            c.id === activeColumnId ? { ...c, cardIds: ids } : c,
          );
          columnsRef.current = next;
          return next;
        }

        const newActiveCardIds = activeCol.cardIds.filter(
          (id) => id !== activeIdStr,
        );
        const overIndex = overCol.cardIds.indexOf(overIdStr);
        const newOverCardIds = overCol.cardIds.filter(
          (id) => id !== activeIdStr,
        );
        if (overIndex >= 0) {
          const insertIndex = Math.max(
            0,
            Math.min(
              overIndex + (shouldInsertAfterOverCard ? 1 : 0),
              newOverCardIds.length,
            ),
          );
          newOverCardIds.splice(insertIndex, 0, activeIdStr);
        } else {
          // When hovering a list container area (including "under the list"),
          // always preview placement at the bottom for deterministic behavior.
          if (overIsList) {
            newOverCardIds.splice(newOverCardIds.length, 0, activeIdStr);
          } else {
            const lastOverCardId = lastOverCardIdRef.current;
            const lastOverColumnId = lastOverColumnIdRef.current;
            const lastOverIsBelow = lastOverIsBelowRef.current;
            const fallbackOverIndex =
              lastOverColumnId === effectiveOverColumnId && lastOverCardId
                ? newOverCardIds.indexOf(lastOverCardId)
                : -1;
            if (fallbackOverIndex >= 0) {
              const insertIndex = Math.max(
                0,
                Math.min(
                  fallbackOverIndex + (lastOverIsBelow ? 1 : 0),
                  newOverCardIds.length,
                ),
              );
              newOverCardIds.splice(insertIndex, 0, activeIdStr);
            } else {
              newOverCardIds.push(activeIdStr);
            }
          }
        }

        const next = prev.map((c) => {
          if (c.id === activeColumnId) {
            return { ...c, cardIds: newActiveCardIds };
          }
          if (c.id === effectiveOverColumnId)
            return { ...c, cardIds: newOverCardIds };
          return c;
        });
        columnsRef.current = next;
        return next;
      });
    },
    [columns],
  );

  const handleDragCancel = useCallback(() => {
    setTimeout(() => {
      hasDraggedRef.current = false;
    }, 0);
    setActiveId(null);
    isTouchCardDragRef.current = false;
    dragPreviewColumnRef.current = null;
    lastOverCardIdRef.current = null;
    lastOverColumnIdRef.current = null;
    lastOverIsBelowRef.current = false;
  }, []);

  const moveCardWithinBoard = useCallback(
    async (request: MoveWithinBoardRequest) => {
      const {
        boardId,
        cardId,
        sourceListId,
        targetListId,
        targetPosition,
        orderedTargetCardIds,
        companyIdValue,
      } = request;
      const operationId = generateUuidV4();
      try {
        const moved = await moveCardInBoard(
          boardId,
          cardId,
          {
            targetListId,
            targetPosition,
            operationId,
          },
          companyIdValue,
        );
        setCards((prev) => {
          const actorName =
            profile?.username?.trim() || profile?.email || 'Someone';
          const sourceTitle =
            columnsRef.current.find((c) => c.id === moved.sourceListId)
              ?.title ?? 'another list';
          const targetTitle =
            columnsRef.current.find((c) => c.id === moved.targetListId)
              ?.title ?? 'a list';
          const moveText =
            moved.sourceListId === moved.targetListId
              ? `reordered this card in ${targetTitle}`
              : `moved this card from ${sourceTitle} to ${targetTitle}`;
          const withFallback = appendLocalActivityIfMissing(
            moved.card,
            moveText,
            actorName,
            profile?.id,
          );
          return { ...prev, [moved.card.id]: withFallback };
        });
        setColumns((prev) => {
          const next = prev.map((c) => {
            if (c.id === moved.sourceListId && c.id === moved.targetListId) {
              return { ...c, cardIds: moved.targetCardIds };
            }
            if (c.id === moved.sourceListId) {
              return { ...c, cardIds: moved.sourceCardIds };
            }
            if (c.id === moved.targetListId) {
              return { ...c, cardIds: moved.targetCardIds };
            }
            return c;
          });
          columnsRef.current = next;
          return next;
        });
      } catch (err) {
        const msg = err instanceof Error ? err.message : '';
        const isMoveRouteMissing =
          /Cannot POST .*\/cards\/.+\/move/i.test(msg) ||
          /404/i.test(msg) ||
          /Not Found/i.test(msg);
        if (!isMoveRouteMissing) {
          throw err;
        }
        // Backward compatibility for backends that don't support /move yet.
        if (sourceListId === targetListId) {
          await reorderCardsInList(
            boardId,
            targetListId,
            orderedTargetCardIds,
            companyIdValue,
          );
          return;
        }
        await updateCard(
          boardId,
          cardId,
          { listId: targetListId, position: targetPosition },
          companyIdValue,
        );
        await reorderCardsInList(
          boardId,
          targetListId,
          orderedTargetCardIds,
          companyIdValue,
        );
      }
    },
    [profile],
  );

  const enqueueCardMoveWithinBoard = useCallback(
    async (request: MoveWithinBoardRequest) => {
      const { cardId } = request;
      let state = queuedCardMovesRef.current.get(cardId);
      if (!state) {
        state = { running: false, latest: null, promise: null };
        queuedCardMovesRef.current.set(cardId, state);
      }
      state.latest = request;
      if (state.running && state.promise) {
        return state.promise;
      }
      state.running = true;
      state.promise = (async () => {
        let lastError: unknown = null;
        try {
          while (state?.latest) {
            const latestRequest = state.latest;
            state.latest = null;
            try {
              await moveCardWithinBoard(latestRequest);
            } catch (err) {
              lastError = err;
            }
          }
        } finally {
          const latestState = queuedCardMovesRef.current.get(cardId);
          if (latestState) {
            latestState.running = false;
            latestState.promise = null;
            if (latestState.latest == null) {
              queuedCardMovesRef.current.delete(cardId);
            }
          }
        }
        if (lastError != null) {
          throw lastError;
        }
      })();
      return state.promise;
    },
    [moveCardWithinBoard],
  );

  const clearCardDragState = useCallback(() => {
    setTimeout(() => {
      hasDraggedRef.current = false;
    }, 0);
    setActiveId(null);
    isTouchCardDragRef.current = false;
    dragPreviewColumnRef.current = null;
  }, []);

  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!activeBoardId || !companyId) {
        clearCardDragState();
        return;
      }
      const activeIdStr = String(active.id);
      const latestColumns = columnsRef.current;
      const isListDrag = latestColumns.some((c) => c.id === activeIdStr);
      const overIdStr = over != null ? String(over.id) : null;
      const overIsList =
        overIdStr != null && latestColumns.some((c) => c.id === overIdStr);

      if (isListDrag) {
        clearCardDragState();
        if (over == null) return;
        const overIdStr = String(over.id);
        if (!latestColumns.some((c) => c.id === overIdStr)) return;

        const oldIndex = latestColumns.findIndex((c) => c.id === activeIdStr);
        const newIndex = latestColumns.findIndex((c) => c.id === overIdStr);
        if (oldIndex === -1 || newIndex === -1) return;
        const newOrder = arrayMove(latestColumns, oldIndex, newIndex);
        const listIds = newOrder.map((c) => c.id);
        try {
          await reorderLists(activeBoardId, listIds, companyId);
        } catch (err) {
          const msg =
            err instanceof Error ? err.message : 'Failed to reorder lists';
          showToast(msg, 'error');
          void loadBoardData(activeBoardId, true);
        }
        return;
      }

      // Cards: persist based on the optimistic preview state in `columns`.
      // This guarantees the saved placement matches what you see while dragging,
      // even if `event.over` is null or resolves to a list container.
      const startColumnId = dragStartColumnIdRef.current;
      const sourceColumnId =
        findColumnId(latestColumns, activeIdStr) ?? startColumnId;
      const lastOverColumnId = lastOverColumnIdRef.current;
      const targetColumnId =
        // If release is directly on a list container (not a card), trust that
        // list immediately.
        overIsList && overIdStr != null
          ? overIdStr
          : // Cross-column moves: always trust the last collided column (it comes
            // from the hovered card), even if the optimistic list state hasn't
            // applied yet.
            lastOverColumnId != null && lastOverColumnId !== sourceColumnId
            ? lastOverColumnId
            : // Same-column moves: be conservative to avoid stale refs pushing
              // to list extremes.
              lastOverColumnId != null &&
                latestColumns.some(
                  (c) =>
                    c.id === lastOverColumnId &&
                    c.cardIds.includes(activeIdStr),
                )
              ? lastOverColumnId
              : sourceColumnId;
      if (!sourceColumnId || !targetColumnId) {
        clearCardDragState();
        return;
      }

      const movedToAnotherList =
        startColumnId != null && startColumnId !== targetColumnId;
      if (movedToAnotherList) {
        scrollBoardToColumn(boardWrapperRef.current, targetColumnId);
      }
      clearCardDragState();
      if (movedToAnotherList) {
        requestAnimationFrame(() => {
          scrollBoardToColumn(boardWrapperRef.current, targetColumnId);
        });
      }

      const targetCol = latestColumns.find((c) => c.id === targetColumnId);
      if (!targetCol) return;

      const lastOverCardId = lastOverCardIdRef.current;
      const lastOverIsBelow = lastOverIsBelowRef.current;

      // Default to the current optimistic order (matches the preview).
      // For same-column moves (up/down), persist exactly this order to avoid
      // the "second closest" off-by-one mismatch.
      let computedTargetCardIds = targetCol.cardIds;

      // Deterministic under-list behavior: if release happened on the list
      // container (not a card), always persist as bottom placement.
      if (overIsList) {
        const baseTargetIds = targetCol.cardIds.filter(
          (id) => id !== activeIdStr,
        );
        computedTargetCardIds = [...baseTargetIds, activeIdStr];
      }

      // For cross-column moves we keep a collision-based fallback, because
      // the optimistic state can be briefly inconsistent when release occurs
      // between cards/containers.
      if (sourceColumnId !== targetColumnId && !overIsList) {
        computedTargetCardIds = targetCol.cardIds;
        if (!computedTargetCardIds.includes(activeIdStr) && lastOverCardId) {
          const baseTargetIds = targetCol.cardIds.filter(
            (id) => id !== activeIdStr,
          );
          const overIndex = baseTargetIds.indexOf(lastOverCardId);
          if (overIndex >= 0) {
            const insertIndex = Math.max(
              0,
              Math.min(
                overIndex + (lastOverIsBelow ? 1 : 0),
                baseTargetIds.length,
              ),
            );
            computedTargetCardIds = [...baseTargetIds];
            computedTargetCardIds.splice(insertIndex, 0, activeIdStr);
          }
        }
      }

      const targetCardIndex = computedTargetCardIds.indexOf(activeIdStr);

      try {
        await enqueueCardMoveWithinBoard({
          boardId: activeBoardId,
          cardId: activeIdStr,
          sourceListId: sourceColumnId,
          targetListId: targetColumnId,
          targetPosition: Math.max(0, targetCardIndex),
          orderedTargetCardIds: computedTargetCardIds,
          companyIdValue: companyId,
        });
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to move card';
        showToast(msg, 'error');
        void loadBoardData(activeBoardId, true);
      }
    },
    [
      activeBoardId,
      companyId,
      showToast,
      loadBoardData,
      enqueueCardMoveWithinBoard,
      clearCardDragState,
    ],
  );

  const handleColumnTitleClick = useCallback(
    (columnId: string) => {
      const col = columns.find((c) => c.id === columnId);
      if (col) {
        setEditingColumnId(columnId);
        setEditingColumnTitle(col.title);
      }
    },
    [columns],
  );

  const handleColumnTitleSubmit = useCallback(async () => {
    if (
      !editingColumnId ||
      !editingColumnTitle.trim() ||
      !activeBoardId ||
      !companyId
    ) {
      setEditingColumnId(null);
      return;
    }
    const trimmed = editingColumnTitle.trim();
    const previousTitle =
      columns.find((c) => c.id === editingColumnId)?.title ?? trimmed;

    // Optimistically update column title.
    setColumns((prev) =>
      prev.map((c) =>
        c.id === editingColumnId ? { ...c, title: trimmed } : c,
      ),
    );
    try {
      await updateList(
        activeBoardId,
        editingColumnId,
        { title: trimmed },
        companyId,
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to rename list';
      showToast(msg, 'error');
      setColumns((prev) =>
        prev.map((c) =>
          c.id === editingColumnId ? { ...c, title: previousTitle } : c,
        ),
      );
    }
    setEditingColumnId(null);
  }, [
    editingColumnId,
    editingColumnTitle,
    activeBoardId,
    companyId,
    columns,
    showToast,
  ]);

  const handleAddColumnClick = useCallback(() => {
    setAddingColumn(true);
    setNewColumnTitle('');
  }, []);

  const handleAddColumnConfirm = useCallback(async () => {
    if (!newColumnTitle.trim() || !activeBoardId || !companyId) return;
    const trimmedTitle = newColumnTitle.trim();
    setAddingColumn(false);
    setNewColumnTitle('');

    try {
      const created = await createList(
        activeBoardId,
        { title: trimmedTitle, position: columns.length },
        companyId,
      );

      // Insert list exactly once. If socket already inserted it, skip.
      setColumns((prev) => {
        if (prev.some((c) => c.id === created.id)) return prev;

        const insertAt = Math.max(
          0,
          Math.min(created.position ?? prev.length, prev.length),
        );
        const col: KanbanColumn = {
          id: created.id,
          title: created.title,
          cardIds: created.cardIds ?? [],
        };

        const next = [...prev];
        next.splice(insertAt, 0, col);
        return next;
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to create list';
      showToast(msg, 'error');
    }
  }, [newColumnTitle, activeBoardId, companyId, columns.length, showToast]);

  const handleAddColumnCancel = useCallback(() => {
    setAddingColumn(false);
    setNewColumnTitle('');
  }, []);

  const handleAddCardClick = useCallback((columnId: string) => {
    setAddingCardColumnId(columnId);
    setNewCardTitle('');
  }, []);

  const handleAddCardConfirm = useCallback(
    async (columnId: string) => {
      if (!newCardTitle.trim() || !activeBoardId || !companyId) return;
      const trimmedTitle = newCardTitle.trim();
      setNewCardTitle('');

      try {
        const created = await createCard(
          activeBoardId,
          columnId,
          { title: trimmedTitle },
          companyId,
        );
        const actorName =
          profile?.username?.trim() || profile?.email || 'Someone';
        const cardWithActivity = appendLocalActivityIfMissing(
          created,
          `added this card to ${columns.find((c) => c.id === columnId)?.title ?? 'a list'}`,
          actorName,
          profile?.id,
        );

        // Insert card exactly once. If socket already inserted it, avoid duplicates.
        setCards((prev) =>
          prev[created.id] ? prev : { ...prev, [created.id]: cardWithActivity },
        );
        setColumns((prev) =>
          prev.map((c) => {
            if (c.id !== columnId) return c;
            if (c.cardIds.includes(created.id)) return c;

            const insertAt =
              typeof created.position === 'number'
                ? Math.max(0, Math.min(created.position, c.cardIds.length))
                : c.cardIds.length;

            const nextIds = [...c.cardIds];
            nextIds.splice(insertAt, 0, created.id);
            return { ...c, cardIds: nextIds };
          }),
        );
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : 'Failed to create card';
        showToast(msg, 'error');
      }
    },
    [newCardTitle, activeBoardId, companyId, showToast, columns, profile],
  );

  const handleAddCardCancel = useCallback(() => {
    setAddingCardColumnId(null);
    setNewCardTitle('');
  }, []);

  const handleArchiveCard = useCallback(
    async (cardId: string) => {
      const card = cards[cardId];
      if (!card || !activeBoardId || !companyId) return;

      const listId = findColumnId(columns, cardId) ?? card.listId ?? null;
      const col = listId ? columns.find((c) => c.id === listId) : null;
      const cardIndex = col ? col.cardIds.indexOf(cardId) : -1;

      const previousCard = card;

      // Optimistically remove from the board (archive panel updates on server response).
      if (listId) {
        setColumns((prev) =>
          prev.map((c) =>
            c.id === listId
              ? { ...c, cardIds: c.cardIds.filter((id) => id !== cardId) }
              : c,
          ),
        );
      }
      setCards((prev) => {
        const next = { ...prev };
        delete next[cardId];
        return next;
      });
      if (selectedCardId === cardId) setSelectedCardIdWrapped(null);

      try {
        const record = await archiveCard(activeBoardId, cardId, companyId);
        const entry: ArchivedCard = {
          id: record.id,
          archivedAt: record.archivedAt,
          card: record.card,
          originalListId: record.originalListId,
          originalListTitle: record.originalListTitle ?? '',
          originalIndex: record.originalPosition ?? 0,
        };
        setArchivedCards((prev) =>
          prev.some((a) => a.id === entry.id) ? prev : [entry, ...prev],
        );
        showToast(
          'Item archived. You can restore it from Archived Items.',
          'success',
        );
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : 'Failed to archive card';
        const msgLower = msg.toLowerCase();

        // If the backend already archived the card (or it was moved away),
        // treat it as idempotent: don't show an error; just reconcile.
        if (
          msgLower.includes('card not found') ||
          msgLower.includes('not found')
        ) {
          void loadBoardData(activeBoardId, true);
          return;
        }

        showToast(msg, 'error');

        // Roll back optimistic board removal.
        setCards((prev) => ({ ...prev, [cardId]: previousCard }));
        if (listId) {
          setColumns((prev) =>
            prev.map((c) => {
              if (c.id !== listId) return c;
              const nextIds = [...c.cardIds];
              if (cardIndex >= 0 && cardIndex <= nextIds.length) {
                nextIds.splice(cardIndex, 0, cardId);
              } else {
                nextIds.push(cardId);
              }
              return { ...c, cardIds: nextIds };
            }),
          );
        }
      }
    },
    [
      cards,
      columns,
      selectedCardId,
      activeBoardId,
      companyId,
      showToast,
      setSelectedCardIdWrapped,
      loadBoardData,
    ],
  );

  const handleArchiveList = useCallback(
    async (columnId: string) => {
      const col = columns.find((c) => c.id === columnId);
      if (!col || !activeBoardId || !companyId) return;

      const originalIndex = columns.findIndex((c) => c.id === columnId);
      const cardsSnapshot: Record<string, KanbanCardData> = {};
      col.cardIds.forEach((cid) => {
        const card = cards[cid];
        if (card) cardsSnapshot[cid] = card;
      });

      // Optimistically remove list and cards from the board.
      setColumns((prev) => prev.filter((c) => c.id !== columnId));
      setCards((prev) => {
        const next = { ...prev };
        col.cardIds.forEach((cid) => delete next[cid]);
        return next;
      });
      if (editingColumnId === columnId) setEditingColumnId(null);
      if (selectedCardId && col.cardIds.includes(selectedCardId)) {
        setSelectedCardIdWrapped(null);
      }

      try {
        await archiveList(activeBoardId, columnId, companyId);
        setArchivedLists((prev) => {
          const entry: ArchivedList = {
            id: columnId,
            archivedAt: new Date().toISOString(),
            column: { ...col },
            originalIndex,
            cardsSnapshot,
          };
          return prev.some((a) => a.id === entry.id) ? prev : [entry, ...prev];
        });
        showToast(
          'Item archived. You can restore it from Archived Items.',
          'success',
        );
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : 'Failed to archive list';
        showToast(msg, 'error');

        // Roll back optimistic board removal.
        setColumns((prev) => {
          const next = [...prev];
          const insertAt = Math.max(0, Math.min(originalIndex, next.length));
          next.splice(insertAt, 0, { ...col });
          return next;
        });
        setCards((prev) => ({ ...prev, ...cardsSnapshot }));
      }
    },
    [
      columns,
      cards,
      editingColumnId,
      selectedCardId,
      showToast,
      activeBoardId,
      companyId,
      setSelectedCardIdWrapped,
      setEditingColumnId,
    ],
  );

  const handleRestoreCard = useCallback(
    async (archived: ArchivedCard, targetListId?: string) => {
      if (!activeBoardId || !companyId) return;
      const listId = targetListId ?? archived.originalListId;
      const col = columns.find((c) => c.id === listId);
      if (!col && !targetListId) {
        setRestoreCardItem(archived);
        setRestoreTargetListId(null);
        return;
      }

      // Optimistically restore on the board immediately.
      const optimisticCardId = archived.card.id;
      const optimisticCard = archived.card;
      const optimisticTargetListId = listId;
      const insertIndex = col ? col.cardIds.length : 0;

      setCards((prev) => {
        // Avoid overwriting if something else created a card with the same id.
        if (prev[optimisticCardId]) return prev;
        return { ...prev, [optimisticCardId]: optimisticCard };
      });
      setColumns((prev) =>
        prev.map((c) => {
          if (c.id !== optimisticTargetListId) return c;
          if (c.cardIds.includes(optimisticCardId)) return c;
          const ids = [...c.cardIds];
          ids.splice(
            Math.max(0, Math.min(insertIndex, ids.length)),
            0,
            optimisticCardId,
          );
          return { ...c, cardIds: ids };
        }),
      );

      try {
        const body =
          targetListId != null
            ? { listId: targetListId, position: 0 }
            : undefined;
        const restored = await restoreArchivedCard(
          activeBoardId,
          archived.id,
          body ?? {},
          companyId,
        );

        // Update archive panel only after server confirms.
        setArchivedCards((prev) => prev.filter((a) => a.id !== archived.id));

        setCards((prev) => ({ ...prev, [restored.id]: restored }));

        // If backend returns a different id, reconcile cardId references in columns.
        if (restored.id !== optimisticCardId) {
          setColumns((prev) =>
            prev.map((c) => {
              if (c.id !== restored.listId) return c;
              return {
                ...c,
                cardIds: c.cardIds.map((id) =>
                  id === optimisticCardId ? restored.id : id,
                ),
              };
            }),
          );
          setCards((prev) => {
            const next = { ...prev };
            delete next[optimisticCardId];
            return next;
          });
        }

        setRestoreCardItem(null);
        setRestoreTargetListId(null);
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : 'Failed to restore card';
        const msgLower = msg.toLowerCase();

        // Backend returns 404 when the archived card was already restored
        // (idempotency). In that case, reconcile instead of rolling back
        // our optimistic insertion (which would remove the card from the board).
        if (
          msgLower.includes('archived card not found') ||
          (msgLower.includes('archived card') &&
            msgLower.includes('not found')) ||
          (msgLower.includes('archived card') && msgLower.includes('not found'))
        ) {
          void loadBoardData(activeBoardId, true);
          setArchivedCards((prev) => prev.filter((a) => a.id !== archived.id));
          setRestoreCardItem(null);
          setRestoreTargetListId(null);
          return;
        }

        showToast(msg, 'error');

        // Roll back optimistic board insertion.
        setCards((prev) => {
          const next = { ...prev };
          delete next[optimisticCardId];
          return next;
        });
        setColumns((prev) =>
          prev.map((c) => {
            if (c.id !== optimisticTargetListId) return c;
            return {
              ...c,
              cardIds: c.cardIds.filter((id) => id !== optimisticCardId),
            };
          }),
        );
      }
    },
    [
      columns,
      activeBoardId,
      companyId,
      showToast,
      loadBoardData,
      setRestoreCardItem,
      setRestoreTargetListId,
    ],
  );

  const handleRestoreCardWithTarget = useCallback(
    (archived: ArchivedCard, targetListId: string) => {
      void handleRestoreCard(archived, targetListId);
      setRestoreCardItem(null);
    },
    [handleRestoreCard],
  );

  const handleRestoreList = useCallback(
    async (archived: ArchivedList, targetIndex?: number) => {
      if (!activeBoardId || !companyId) return;
      const optimisticColumn = archived.column;
      const optimisticColumnId = optimisticColumn.id;
      const optimisticCardsSnapshot = archived.cardsSnapshot;
      const previousCardsById: Record<string, KanbanCardData | undefined> = {};
      Object.keys(optimisticCardsSnapshot).forEach((id) => {
        previousCardsById[id] = cards[id];
      });

      const previousColumn = columns.find((c) => c.id === optimisticColumnId);
      const previousColumnIndex = previousColumn
        ? columns.findIndex((c) => c.id === optimisticColumnId)
        : -1;
      const previousColumnCardIds = previousColumn?.cardIds ?? [];

      const position =
        targetIndex ?? Math.min(archived.originalIndex, columns.length);

      // Optimistically restore on the board immediately.
      setColumns((prev) => {
        const existingIndex = prev.findIndex(
          (c) => c.id === optimisticColumnId,
        );

        const insertAt = Math.max(
          0,
          Math.min(position, prev.length - (existingIndex >= 0 ? 1 : 0)),
        );

        const next = [...prev];
        if (existingIndex >= 0) next.splice(existingIndex, 1);

        // Merge card order: snapshot first, then keep any existing cards.
        const nextCardIds: string[] = [];
        const seen = new Set<string>();
        const snapshotIds = optimisticColumn.cardIds ?? [];
        for (const id of snapshotIds) {
          if (seen.has(id)) continue;
          seen.add(id);
          nextCardIds.push(id);
        }
        const remainingIds =
          (existingIndex >= 0 ? previousColumnCardIds : []) ?? [];
        for (const id of remainingIds) {
          if (seen.has(id)) continue;
          seen.add(id);
          nextCardIds.push(id);
        }

        next.splice(insertAt, 0, { ...optimisticColumn, cardIds: nextCardIds });
        return next;
      });
      setCards((prev) => ({ ...prev, ...optimisticCardsSnapshot }));
      try {
        await restoreArchivedList(
          activeBoardId,
          archived.id,
          { position },
          companyId,
        );
        void loadBoardData(activeBoardId, true);
        setArchivedLists((prev) => prev.filter((a) => a.id !== archived.id));
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : 'Failed to restore list';
        const msgLower = msg.toLowerCase();

        // Idempotent restore: archived list already restored/deleted.
        if (
          msgLower.includes('archived list not found') ||
          (msgLower.includes('archived list') && msgLower.includes('not found'))
        ) {
          void loadBoardData(activeBoardId, true);
          setArchivedLists((prev) => prev.filter((a) => a.id !== archived.id));
          return;
        }

        showToast(msg, 'error');

        // Roll back optimistic board insertion.
        setColumns((prev) => {
          const next = [...prev];
          const existingIndex = next.findIndex(
            (c) => c.id === optimisticColumnId,
          );
          if (existingIndex === -1) return prev;

          next.splice(existingIndex, 1);
          if (previousColumn && previousColumnIndex >= 0) {
            next.splice(
              Math.max(0, Math.min(previousColumnIndex, next.length)),
              0,
              { ...previousColumn, cardIds: previousColumnCardIds },
            );
          }
          return next;
        });
        setCards((prev) => {
          const next = { ...prev };
          Object.keys(optimisticCardsSnapshot).forEach((id) => {
            const prevCard = previousCardsById[id];
            if (prevCard) next[id] = prevCard;
            else delete next[id];
          });
          return next;
        });
      }
    },
    [
      columns,
      cards,
      activeBoardId,
      companyId,
      loadBoardData,
      showToast,
      setArchivedLists,
      setColumns,
      setCards,
    ],
  );

  const handlePermanentDeleteArchivedCard = useCallback(
    async (archived: ArchivedCard) => {
      if (!activeBoardId || !companyId) return;
      const previousArchivedCard = archivedCards.find(
        (a) => a.id === archived.id,
      );

      // Optimistically remove from the archive panel.
      setArchivedCards((prev) => prev.filter((a) => a.id !== archived.id));
      try {
        await deleteArchivedCard(activeBoardId, archived.id, companyId);
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : 'Failed to delete card';
        showToast(msg, 'error');

        // Roll back optimistic removal by re-inserting only the missing item.
        if (previousArchivedCard) {
          setArchivedCards((prev) => {
            if (prev.some((a) => a.id === previousArchivedCard.id)) return prev;
            return [previousArchivedCard, ...prev];
          });
        }
      }
    },
    [activeBoardId, companyId, showToast, archivedCards],
  );

  const handlePermanentDeleteArchivedList = useCallback(
    async (archived: ArchivedList) => {
      if (!activeBoardId || !companyId) return;
      const previousArchivedList = archivedLists.find(
        (a) => a.id === archived.id,
      );

      // Optimistically remove from the archive panel.
      setArchivedLists((prev) => prev.filter((a) => a.id !== archived.id));
      try {
        await deleteArchivedList(activeBoardId, archived.id, companyId);
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : 'Failed to delete list';
        showToast(msg, 'error');

        // Roll back optimistic removal by re-inserting only the missing item.
        if (previousArchivedList) {
          setArchivedLists((prev) => {
            if (prev.some((a) => a.id === previousArchivedList.id)) return prev;
            return [previousArchivedList, ...prev];
          });
        }
      }
    },
    [activeBoardId, companyId, showToast, archivedLists],
  );

  const handleConfirmPermanentDelete = useCallback(async () => {
    if (!deleteConfirmItem) return;
    if (deleteConfirmItem.type === 'card')
      await handlePermanentDeleteArchivedCard(deleteConfirmItem.item);
    else await handlePermanentDeleteArchivedList(deleteConfirmItem.item);
    setDeleteConfirmItem(null);
  }, [
    deleteConfirmItem,
    handlePermanentDeleteArchivedCard,
    handlePermanentDeleteArchivedList,
  ]);

  const handleConfirmDeleteBoard = useCallback(async () => {
    if (!boardToDelete || !companyId) return;
    try {
      await deleteBoard(boardToDelete.id, companyId);
      setBoards((prev) => prev.filter((b) => b.id !== boardToDelete.id));
      if (activeBoardId === boardToDelete.id) {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete(URL_PARAM_KEYS.boardShort);
          next.delete(URL_PARAM_KEYS.boardLegacy);
          return withCompanyAliasLast(next);
        });
      }
      setBoardToDelete(null);
      showToast('Board deleted.', 'success');
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Failed to delete board.';
      showToast(msg, 'error');
    }
  }, [boardToDelete, companyId, activeBoardId, setSearchParams, showToast]);

  const handleLinkUserToBoard = useCallback(
    async (email: string) => {
      const trimmed = email.trim();
      if (!activeBoardId || !companyId || !trimmed || addingMember) return;
      setAddingMember(true);
      let optimisticMemberId: string | null = null;
      try {
        const companyUsers = await companyService.getCompanyUsers(companyId);
        const user = companyUsers.find(
          (u) => u.email.toLowerCase() === trimmed.toLowerCase(),
        );
        if (!user) {
          showToast(
            'User not found. Link them to the company first from Company Users.',
            'error',
          );
          return;
        }
        const alreadyMember = boardMembers.some((m) => {
          if (m.userId) return m.userId === user.id;
          if (m.email) {
            return m.email.toLowerCase() === user.email.toLowerCase();
          }
          return false;
        });
        if (alreadyMember) return;

        // Optimistically add to the board members list.
        optimisticMemberId = `temp-member-${generateId()}`;
        const optimisticMember: BoardMember = {
          id: optimisticMemberId,
          name: user.username ?? user.email,
          userId: user.id,
          email: user.email,
        };
        setBoardMembers((prev) => [...prev, optimisticMember]);
        setAddMemberEmail('');

        const created = await addBoardMember(
          activeBoardId,
          { userId: user.id, name: user.username },
          companyId,
        );

        // Replace temp member with the server member and dedupe by id.
        setBoardMembers((prev) => {
          const mapped = prev.map((m) =>
            m.id === optimisticMemberId ? created : m,
          );
          const seen = new Set<string>();
          return mapped.filter((m) => {
            if (seen.has(m.id)) return false;
            seen.add(m.id);
            return true;
          });
        });
        showToast('Member added to board.', 'success');
      } catch (err) {
        if (optimisticMemberId) {
          setBoardMembers((prev) =>
            prev.filter((m) => m.id !== optimisticMemberId),
          );
        }
        const msg =
          err instanceof Error ? err.message : 'Failed to add member to board.';
        showToast(msg, 'error');
      } finally {
        setAddingMember(false);
      }
    },
    [
      activeBoardId,
      addingMember,
      boardMembers,
      companyId,
      companyService,
      setBoardMembers,
      showToast,
      setAddMemberEmail,
    ],
  );

  const handleConfirmRemoveBoardMember = useCallback(async () => {
    if (!memberToRemove || !activeBoardId || !companyId) return;
    const prevMembers = boardMembers;
    setBoardMembers((prev) => prev.filter((m) => m.id !== memberToRemove.id));
    setMemberToRemove(null);
    try {
      await removeBoardMember(activeBoardId, memberToRemove.id, companyId);
      showToast('Member removed from board.', 'success');
    } catch (err) {
      setBoardMembers(prevMembers);
      const msg =
        err instanceof Error
          ? err.message
          : 'Failed to remove member from board.';
      showToast(msg, 'error');
    }
  }, [activeBoardId, boardMembers, companyId, memberToRemove, showToast]);

  const handleArchiveAllCardsInList = useCallback(
    async (columnId: string) => {
      const col = columns.find((c) => c.id === columnId);
      if (!col || !activeBoardId || !companyId) return;
      // Stable de-dupe to avoid calling the backend twice for the same card id.
      const cardIdsToArchive: string[] = [];
      const seen = new Set<string>();
      for (const cid of col.cardIds) {
        if (seen.has(cid)) continue;
        seen.add(cid);
        cardIdsToArchive.push(cid);
      }

      const cardsSnapshotById: Record<string, KanbanCardData> = {};
      cardIdsToArchive.forEach((cid) => {
        const card = cards[cid];
        if (card) cardsSnapshotById[cid] = card;
      });
      const indexById = new Map(
        cardIdsToArchive.map((id, i) => [id, i] as const),
      );

      // Optimistically remove from the board immediately.
      setColumns((prev) =>
        prev.map((c) => (c.id === columnId ? { ...c, cardIds: [] } : c)),
      );
      setCards((prev) => {
        const next = { ...prev };
        cardIdsToArchive.forEach((cid) => delete next[cid]);
        return next;
      });
      if (selectedCardId && cardIdsToArchive.includes(selectedCardId)) {
        setSelectedCardIdWrapped(null);
      }

      let successCount = 0;
      let failureCount = 0;

      for (const cid of cardIdsToArchive) {
        try {
          const record = await archiveCard(activeBoardId, cid, companyId);
          const entry: ArchivedCard = {
            id: record.id,
            archivedAt: record.archivedAt,
            card: record.card,
            originalListId: record.originalListId,
            originalListTitle: record.originalListTitle ?? col.title,
            originalIndex: record.originalPosition ?? 0,
          };
          setArchivedCards((prev) =>
            prev.some((a) => a.id === entry.id) ? prev : [entry, ...prev],
          );
          successCount += 1;
        } catch (err) {
          const msg =
            err instanceof Error ? err.message : 'Failed to archive card';
          const msgLower = msg.toLowerCase();

          // If the card was already archived/missing, treat it as success.
          if (
            msgLower.includes('card not found') ||
            msgLower.includes('not found')
          ) {
            successCount += 1;
            continue;
          }

          failureCount += 1;
          showToast(msg, 'error');

          // Roll back only the failed card.
          const snapshot = cardsSnapshotById[cid];
          if (snapshot) {
            setCards((prev) => ({ ...prev, [cid]: snapshot }));
            setColumns((prev) =>
              prev.map((c) => {
                if (c.id !== columnId) return c;
                const ids = [...c.cardIds, cid];
                ids.sort((a, b) => {
                  const ia = indexById.get(a);
                  const ib = indexById.get(b);
                  return (
                    (ia ?? Number.MAX_SAFE_INTEGER) -
                    (ib ?? Number.MAX_SAFE_INTEGER)
                  );
                });
                return { ...c, cardIds: ids };
              }),
            );
          }
        }
      }

      if (successCount > 0 && failureCount === 0) {
        showToast(
          'Cards archived. You can restore them from Archived Items.',
          'success',
        );
      } else if (successCount > 0 && failureCount > 0) {
        showToast(
          `Cards archived (${successCount}/${successCount + failureCount}). Some items failed and were restored.`,
          'error',
        );
      }
    },
    [
      columns,
      activeBoardId,
      companyId,
      cards,
      selectedCardId,
      showToast,
      setSelectedCardIdWrapped,
    ],
  );

  const handleArchiveListWithConfirm = useCallback((columnId: string) => {
    setArchiveListConfirmColumnId(columnId);
  }, []);

  const handleConfirmArchiveList = useCallback(() => {
    if (archiveListConfirmColumnId) {
      void handleArchiveList(archiveListConfirmColumnId);
      setArchiveListConfirmColumnId(null);
    }
  }, [archiveListConfirmColumnId, handleArchiveList]);

  const handleCardClick = useCallback(
    (cardId: string) => {
      if (hasDraggedRef.current) return;
      setSelectedCardIdWrapped(cardId);
    },
    [setSelectedCardIdWrapped],
  );

  const handleUpdateCard = useCallback(
    async (cardId: string, updates: KanbanCardPatchPayload) => {
      if (!activeBoardId || !companyId) return;
      const previousCard = cards[cardId];
      const { advanceDueToNextRecurrence, ...optimisticRest } = updates;
      setCards((prev) => {
        const card = prev[cardId];
        if (!card) return prev;
        return { ...prev, [cardId]: { ...card, ...optimisticRest } };
      });
      try {
        const updated = await updateCard(
          activeBoardId,
          cardId,
          {
            title: updates.title,
            description: updates.description,
            labelIds: updates.labelIds,
            memberIds: updates.memberIds,
            checklist: updates.checklist,
            comments: updates.comments,
            startAt: updates.startAt,
            dueAt: updates.dueAt,
            reminderLeadValue: updates.reminderLeadValue ?? undefined,
            reminderLeadUnit: updates.reminderLeadUnit ?? undefined,
            recurrence: updates.recurrence,
            advanceDueToNextRecurrence,
          },
          companyId,
        );
        setCards((prev) => {
          const prevCard = prev[cardId];
          let merged = mergeCardCommentsPreservingAttachments(
            updated,
            prevCard ?? undefined,
          );
          const actorName =
            profile?.username?.trim() || profile?.email || 'Someone';
          if (updates.description !== undefined) {
            const descriptionText = updates.description?.trim()
              ? 'updated the description'
              : 'cleared the description';
            if (!hasActivityText(merged, descriptionText)) {
              merged = appendLocalActivityIfMissing(
                merged,
                descriptionText,
                actorName,
                profile?.id,
              );
            }
          }
          if (updates.title !== undefined) {
            const titleText = `renamed this card to "${updates.title}"`;
            if (!hasActivityText(merged, titleText)) {
              merged = appendLocalActivityIfMissing(
                merged,
                titleText,
                actorName,
                profile?.id,
              );
            }
          }
          if (updates.memberIds !== undefined && previousCard) {
            const prevSet = new Set(previousCard.memberIds ?? []);
            const nextSet = new Set(updates.memberIds ?? []);
            const addedIds = [...nextSet].filter((id) => !prevSet.has(id));
            const removedIds = [...prevSet].filter((id) => !nextSet.has(id));
            const memberNameById = new Map(
              boardMembers.map((member) => [member.id, member.name]),
            );
            const formatMemberNames = (ids: string[]) =>
              ids
                .map((id) => memberNameById.get(id) ?? `Unknown member (${id})`)
                .join(', ');

            if (addedIds.length > 0) {
              const addedText = `added member${addedIds.length > 1 ? 's' : ''}: ${formatMemberNames(addedIds)}`;
              if (!hasActivityText(merged, addedText)) {
                merged = appendLocalActivityIfMissing(
                  merged,
                  addedText,
                  actorName,
                  profile?.id,
                );
              }
            }
            if (removedIds.length > 0) {
              const removedText = `removed member${removedIds.length > 1 ? 's' : ''}: ${formatMemberNames(removedIds)}`;
              if (!hasActivityText(merged, removedText)) {
                merged = appendLocalActivityIfMissing(
                  merged,
                  removedText,
                  actorName,
                  profile?.id,
                );
              }
            }
          }
          return { ...prev, [cardId]: merged };
        });
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : 'Failed to update card';
        showToast(msg, 'error');
        if (previousCard) {
          setCards((prev) => ({ ...prev, [cardId]: previousCard }));
        }
      }
    },
    [activeBoardId, companyId, showToast, cards, profile, boardMembers],
  );

  const handleBoardCardTitleSubmit = useCallback(() => {
    if (!editingCardTitleId) return;
    const trimmed = editingCardTitleValue.trim();
    if (trimmed) {
      void handleUpdateCard(editingCardTitleId, { title: trimmed });
    }
    setEditingCardTitleId(null);
  }, [editingCardTitleId, editingCardTitleValue, handleUpdateCard]);

  const handleBoardCardTitleCancel = useCallback(() => {
    setEditingCardTitleId(null);
  }, []);

  const handleAddComment = useCallback(
    async (
      cardId: string,
      text: string,
      attachments?: KanbanCardCommentAttachment[],
    ) => {
      if (!activeBoardId || !companyId) return;
      const previousCard = cards[cardId];
      if (!previousCard) return;

      const optimisticCommentId = `temp-comment-${generateId()}`;
      const authorName = profile?.username ?? 'Someone';
      const authorId =
        profile?.authUser != null
          ? typeof profile.authUser === 'string'
            ? profile.authUser
            : profile.authUser.id
          : undefined;

      const optimisticComment: KanbanCardComment = {
        id: optimisticCommentId,
        authorName,
        text,
        createdAt: new Date().toISOString(),
        ...(authorId ? { authorId } : {}),
        ...(attachments?.length ? { attachments } : {}),
      };

      // Optimistically add the comment immediately.
      setCards((prev) => {
        const card = prev[cardId];
        if (!card) return prev;
        const prevComments = card.comments ?? [];
        return {
          ...prev,
          [cardId]: { ...card, comments: [...prevComments, optimisticComment] },
        };
      });

      try {
        const updated = await addCardComment(
          activeBoardId,
          cardId,
          { text, attachments },
          companyId,
        );
        setCards((prev) => {
          const merged = mergeCardCommentsPreservingAttachments(
            updated,
            prev[cardId],
          );
          return { ...prev, [cardId]: merged };
        });
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : 'Failed to add comment';
        showToast(msg, 'error');
        // Roll back to previous card state.
        setCards((prev) => {
          if (!previousCard) return prev;
          return { ...prev, [cardId]: previousCard };
        });
      }
    },
    [activeBoardId, companyId, showToast, cards, profile],
  );

  const handleMoveCard = useCallback(
    async (
      cardId: string,
      targetColumnId: string,
      targetBoardId?: string,
      targetPosition0Based?: number,
    ) => {
      const destinationBoardId = targetBoardId ?? activeBoardId ?? '';
      const isCrossBoardMove =
        Boolean(targetBoardId) &&
        Boolean(activeBoardId) &&
        targetBoardId !== activeBoardId;
      const sourceColumnId = findColumnId(columns, cardId);
      if (!sourceColumnId || !activeBoardId || !companyId) return;
      const targetCol = columns.find((c) => c.id === targetColumnId);
      if (!isCrossBoardMove && !targetCol) return;
      const isSameListMove =
        !isCrossBoardMove && sourceColumnId === targetColumnId;

      const requestedPosition = Number.isFinite(targetPosition0Based)
        ? (targetPosition0Based as number)
        : 0;

      if (isCrossBoardMove) {
        const previousColumns = columns;
        const previousCard = cards[cardId];
        if (!previousCard) return;
        setColumns((prev) =>
          prev.map((c) =>
            c.id === sourceColumnId
              ? { ...c, cardIds: c.cardIds.filter((id) => id !== cardId) }
              : c,
          ),
        );
        setCards((prev) => {
          const next = { ...prev };
          delete next[cardId];
          return next;
        });
        if (selectedCardId === cardId) {
          setSelectedCardIdWrapped(null);
        }
        try {
          // Fetch destination list to build the desired ordering.
          const destLists = await fetchLists(destinationBoardId, companyId);
          const destList = destLists.find((l) => l.id === targetColumnId);
          const destCardIds = destList?.cardIds ?? [];
          const destN = destCardIds.length;
          const insertIndex = Math.max(0, Math.min(requestedPosition, destN));
          const orderedDestinationCardIds = [...destCardIds];
          orderedDestinationCardIds.splice(insertIndex, 0, cardId);

          await updateCard(
            activeBoardId,
            cardId,
            {
              targetBoardId: destinationBoardId,
              listId: targetColumnId,
              position: insertIndex,
            },
            companyId,
          );

          // Reorder to ensure other cards shift around the inserted card.
          await reorderCardsInList(
            destinationBoardId,
            targetColumnId,
            orderedDestinationCardIds,
            companyId,
          );
        } catch (err) {
          const msg =
            err instanceof Error ? err.message : 'Failed to move card';
          showToast(msg, 'error');
          setColumns(previousColumns);
          setCards((prev) => ({ ...prev, [cardId]: previousCard }));
        }
        return;
      }

      const safeTargetCol = targetCol as KanbanColumn;
      const destCardIdsWithoutCard = (safeTargetCol.cardIds ?? []).filter(
        (id) => id !== cardId,
      );
      const destN = destCardIdsWithoutCard.length;
      const insertIndex = Math.max(0, Math.min(requestedPosition, destN));
      const newTargetCardIds = [...destCardIdsWithoutCard];
      newTargetCardIds.splice(insertIndex, 0, cardId);

      if (isSameListMove) {
        const currentIds = safeTargetCol.cardIds ?? [];
        const unchanged =
          currentIds.length === newTargetCardIds.length &&
          currentIds.every((id, idx) => id === newTargetCardIds[idx]);
        if (unchanged) return;
      }

      setColumns((prev) =>
        prev.map((c) => {
          if (isSameListMove && c.id === targetColumnId) {
            return { ...c, cardIds: newTargetCardIds };
          }
          if (c.id === sourceColumnId)
            return { ...c, cardIds: c.cardIds.filter((id) => id !== cardId) };
          if (c.id === targetColumnId)
            return { ...c, cardIds: newTargetCardIds };
          return c;
        }),
      );
      try {
        await enqueueCardMoveWithinBoard({
          boardId: activeBoardId,
          cardId,
          sourceListId: sourceColumnId,
          targetListId: targetColumnId,
          targetPosition: insertIndex,
          orderedTargetCardIds: newTargetCardIds,
          companyIdValue: companyId,
        });
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to move card';
        showToast(msg, 'error');
        void loadBoardData(activeBoardId, true);
      }
    },
    [
      columns,
      activeBoardId,
      companyId,
      showToast,
      loadBoardData,
      cards,
      enqueueCardMoveWithinBoard,
      selectedCardId,
      setSelectedCardIdWrapped,
    ],
  );

  const getBoardListsForMove = useCallback(
    async (boardId: string): Promise<KanbanColumn[]> => {
      if (!boardId) return [];
      if (boardId === activeBoardId) {
        return columns.map((c) => ({ ...c }));
      }
      if (!companyId) return [];
      const lists = await fetchLists(boardId, companyId);
      return lists
        .sort((a, b) => a.position - b.position)
        .map((l) => ({
          id: l.id,
          title: l.title,
          cardIds: l.cardIds ?? [],
        }));
    },
    [activeBoardId, columns, companyId],
  );

  const handleListMenuAction = useCallback(
    (
      columnId: string,
      action: 'addCard' | 'copyList' | 'moveList' | 'moveAllCards' | 'sortBy',
    ) => {
      if (action === 'addCard') {
        setAddingCardColumnId(columnId);
        setNewCardTitle('');
        setListMenuAnchor(null);
        return;
      }
      const col = columns.find((c) => c.id === columnId);
      if (!col) return;
      setListMenuView(action);
      if (action === 'copyList') setCopyListName(`Copy of ${col.title}`);
      if (action === 'moveList')
        setMoveListPosition(columns.findIndex((c) => c.id === columnId));
      if (action === 'moveAllCards') setMoveAllCardsTargetId('');
      if (action === 'sortBy') setSortByOption('titleAsc');
    },
    [columns],
  );

  const handleCopyListCreate = useCallback(async () => {
    if (
      !listMenuAnchor ||
      listMenuView !== 'copyList' ||
      !copyListName.trim() ||
      !activeBoardId ||
      !companyId
    )
      return;
    const trimmedTitle = copyListName.trim();
    const sourceCol = columns.find((c) => c.id === listMenuAnchor.columnId);
    if (!sourceCol) return;

    const optimisticListId = `temp-copy-list-${generateId()}`;
    const optimisticCardIds = sourceCol.cardIds.map(
      () => `temp-copy-card-${generateId()}`,
    );

    const optimisticCards: Record<string, KanbanCardData> = {};
    sourceCol.cardIds.forEach((origCardId, i) => {
      const origCard = cards[origCardId];
      if (!origCard) return;
      const tempId = optimisticCardIds[i];
      optimisticCards[tempId] = {
        ...origCard,
        id: tempId,
        listId: optimisticListId,
        position: i,
      };
    });

    const optimisticColumn: KanbanColumn = {
      id: optimisticListId,
      title: trimmedTitle,
      cardIds: optimisticCardIds.filter((id) => Boolean(optimisticCards[id])),
    };

    // Optimistically show the copied list and its cards.
    setColumns((prev) => [...prev, optimisticColumn]);
    setCards((prev) => ({ ...prev, ...optimisticCards }));
    setListMenuAnchor(null);
    setListMenuView('main');
    setCopyListName('');

    try {
      await copyList(
        activeBoardId,
        listMenuAnchor.columnId,
        { title: trimmedTitle },
        companyId,
      );
      // Reconcile ids/positions from the backend.
      await loadBoardData(activeBoardId, true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to copy list';
      showToast(msg, 'error');

      // Roll back optimistic temp list and cards.
      setColumns((prev) => prev.filter((c) => c.id !== optimisticListId));
      setCards((prev) => {
        const next = { ...prev };
        Object.keys(optimisticCards).forEach((id) => delete next[id]);
        return next;
      });
    }
  }, [
    listMenuAnchor,
    listMenuView,
    copyListName,
    activeBoardId,
    companyId,
    columns,
    cards,
    loadBoardData,
    showToast,
  ]);

  const handleMoveListToPosition = useCallback(async () => {
    if (
      !listMenuAnchor ||
      listMenuView !== 'moveList' ||
      !activeBoardId ||
      !companyId
    )
      return;
    const fromIndex = columns.findIndex(
      (c) => c.id === listMenuAnchor.columnId,
    );
    if (fromIndex === -1) return;
    const toIndex = Math.max(0, Math.min(moveListPosition, columns.length - 1));
    if (fromIndex === toIndex) {
      setListMenuAnchor(null);
      setListMenuView('main');
      return;
    }

    const prevColumns = columns;
    const newOrder = arrayMove(columns, fromIndex, toIndex);
    const listIds = newOrder.map((c) => c.id);

    // Optimistically apply.
    setColumns(newOrder);
    setListMenuAnchor(null);
    setListMenuView('main');
    try {
      await reorderLists(activeBoardId, listIds, companyId);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to move list';
      showToast(msg, 'error');
      setColumns(prevColumns);
    }
  }, [
    listMenuAnchor,
    listMenuView,
    columns,
    moveListPosition,
    activeBoardId,
    companyId,
    showToast,
  ]);

  const handleMoveAllCardsToTarget = useCallback(async () => {
    if (
      !listMenuAnchor ||
      listMenuView !== 'moveAllCards' ||
      !moveAllCardsTargetId ||
      !activeBoardId ||
      !companyId
    )
      return;
    const sourceCol = columns.find((c) => c.id === listMenuAnchor.columnId);
    const targetCol = columns.find((c) => c.id === moveAllCardsTargetId);
    if (!sourceCol || !targetCol || sourceCol.id === targetCol.id) return;

    const prevColumns = columns;
    const optimisticTargetCardIds = [
      ...targetCol.cardIds,
      ...sourceCol.cardIds,
    ];

    // Optimistically update the board immediately.
    setColumns((prev) =>
      prev.map((c) => {
        if (c.id === sourceCol.id) return { ...c, cardIds: [] };
        if (c.id === targetCol.id)
          return { ...c, cardIds: optimisticTargetCardIds };
        return c;
      }),
    );
    setCards((prev) => {
      const next = { ...prev };
      const movedCards = sourceCol.cardIds;
      for (let i = 0; i < movedCards.length; i++) {
        const cardId = movedCards[i];
        const current = next[cardId];
        if (!current) continue;
        next[cardId] = {
          ...current,
          listId: targetCol.id,
          position: targetCol.cardIds.length + i,
        };
      }
      return next;
    });
    setListMenuAnchor(null);
    setListMenuView('main');
    setMoveAllCardsTargetId('');
    try {
      for (let i = 0; i < sourceCol.cardIds.length; i++) {
        const cardId = sourceCol.cardIds[i];
        await updateCard(
          activeBoardId,
          cardId,
          { listId: targetCol.id, position: targetCol.cardIds.length + i },
          companyId,
        );
      }
      await reorderCardsInList(
        activeBoardId,
        targetCol.id,
        optimisticTargetCardIds,
        companyId,
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to move cards';
      showToast(msg, 'error');
      // Conservative reconciliation: reload the whole board.
      void loadBoardData(activeBoardId, true);
      // Also revert immediately for a snappier feel.
      setColumns(prevColumns);
    }
  }, [
    listMenuAnchor,
    listMenuView,
    columns,
    moveAllCardsTargetId,
    activeBoardId,
    companyId,
    showToast,
    loadBoardData,
  ]);

  const handleSortListBy = useCallback(async () => {
    if (
      !listMenuAnchor ||
      listMenuView !== 'sortBy' ||
      !activeBoardId ||
      !companyId
    )
      return;
    const col = columns.find((c) => c.id === listMenuAnchor.columnId);
    if (!col) return;
    const previousCardIds = col.cardIds;
    const sortedIds = [...col.cardIds].sort((a, b) => {
      const cardA = cards[a];
      const cardB = cards[b];
      if (!cardA || !cardB) return 0;

      if (sortByOption === 'startAsc' || sortByOption === 'startDesc') {
        const startA = cardA.startAt ? new Date(cardA.startAt).getTime() : NaN;
        const startB = cardB.startAt ? new Date(cardB.startAt).getTime() : NaN;
        const hasStartA = !Number.isNaN(startA);
        const hasStartB = !Number.isNaN(startB);

        // Keep cards without start dates at the end in both start-date modes.
        if (!hasStartA && !hasStartB) return 0;
        if (!hasStartA) return 1;
        if (!hasStartB) return -1;

        return sortByOption === 'startAsc' ? startA - startB : startB - startA;
      }

      if (sortByOption === 'dueAsc' || sortByOption === 'dueDesc') {
        const dueA = cardA.dueAt ? new Date(cardA.dueAt).getTime() : NaN;
        const dueB = cardB.dueAt ? new Date(cardB.dueAt).getTime() : NaN;
        const hasDueA = !Number.isNaN(dueA);
        const hasDueB = !Number.isNaN(dueB);

        // Keep cards without due dates at the end in both due-date modes.
        if (!hasDueA && !hasDueB) return 0;
        if (!hasDueA) return 1;
        if (!hasDueB) return -1;

        return sortByOption === 'dueAsc' ? dueA - dueB : dueB - dueA;
      }

      const cmp = cardA.title.localeCompare(cardB.title, undefined, {
        sensitivity: 'base',
      });
      return sortByOption === 'titleAsc' ? cmp : -cmp;
    });

    // Optimistically apply the ordering.
    setColumns((prev) =>
      prev.map((c) =>
        c.id === listMenuAnchor.columnId ? { ...c, cardIds: sortedIds } : c,
      ),
    );
    setListMenuAnchor(null);
    setListMenuView('main');
    try {
      await reorderCardsInList(
        activeBoardId,
        listMenuAnchor.columnId,
        sortedIds,
        companyId,
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to sort list';
      showToast(msg, 'error');
      setColumns((prev) =>
        prev.map((c) =>
          c.id === listMenuAnchor.columnId
            ? { ...c, cardIds: previousCardIds }
            : c,
        ),
      );
    }
  }, [
    listMenuAnchor,
    listMenuView,
    columns,
    cards,
    sortByOption,
    activeBoardId,
    companyId,
    showToast,
  ]);

  const handleOpenListMenu = useCallback(
    (columnId: string, e: React.MouseEvent) => {
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      setListMenuAnchor({ columnId, rect });
      setListMenuView('main');
    },
    [],
  );

  const handleOpenCardMenu = useCallback(
    (cardId: string, currentColumnId: string, e: React.MouseEvent) => {
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      setCardMenuAnchor({ cardId, currentColumnId, rect });
      setCardMenuView('main');
    },
    [],
  );

  const handleBoardMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    const target = e.target as HTMLElement;
    if (
      target.closest('[data-kanban-column="true"]') ||
      target.closest('button') ||
      target.closest('input') ||
      target.closest('textarea')
    )
      return;
    const el = boardWrapperRef.current;
    if (!el) return;
    scrollRef.current = {
      isDown: true,
      startPageX: e.pageX,
      scrollLeft: el.scrollLeft,
    };
  }, []);

  const handleBoardMouseMove = useCallback((e: React.MouseEvent) => {
    if (!scrollRef.current.isDown) return;
    e.preventDefault();
    const el = boardWrapperRef.current;
    if (!el) return;
    const walk = e.pageX - scrollRef.current.startPageX;
    el.scrollLeft = scrollRef.current.scrollLeft - walk;
  }, []);

  const handleBoardMouseUp = useCallback(() => {
    scrollRef.current.isDown = false;
  }, []);

  const handleBoardMouseLeave = useCallback(() => {
    scrollRef.current.isDown = false;
  }, []);

  const archiveCount = archivedCards.length + archivedLists.length;
  const activeCard = activeId ? cards[activeId] : null;
  const activeListColumn = activeId
    ? (columns.find((c) => c.id === activeId) ?? null)
    : null;
  const selectedCard = selectedCardId ? cards[selectedCardId] : null;
  const selectedColumnTitle = selectedCardId
    ? (findColumnId(columns, selectedCardId) &&
        columns.find((c) => c.id === findColumnId(columns, selectedCardId))
          ?.title) ||
      ''
    : '';

  const archiveSearchLower = archiveSearch.trim().toLowerCase();
  const filteredArchivedCards = useMemo(() => {
    let list = archivedCards.filter(
      (a) =>
        !archiveSearchLower ||
        a.card.title.toLowerCase().includes(archiveSearchLower) ||
        a.originalListTitle.toLowerCase().includes(archiveSearchLower),
    );
    if (archiveSort === 'name')
      list = [...list].sort((a, b) =>
        a.card.title.localeCompare(b.card.title, undefined, {
          sensitivity: 'base',
        }),
      );
    else if (archiveSort !== 'type')
      list = [...list].sort((a, b) => (b.archivedAt < a.archivedAt ? -1 : 1));
    return list;
  }, [archivedCards, archiveSearchLower, archiveSort]);
  const filteredArchivedLists = useMemo(() => {
    let list = archivedLists.filter(
      (a) =>
        !archiveSearchLower ||
        a.column.title.toLowerCase().includes(archiveSearchLower),
    );
    if (archiveSort === 'name')
      list = [...list].sort((a, b) =>
        a.column.title.localeCompare(b.column.title, undefined, {
          sensitivity: 'base',
        }),
      );
    else if (archiveSort !== 'type')
      list = [...list].sort((a, b) => (b.archivedAt < a.archivedAt ? -1 : 1));
    return list;
  }, [archivedLists, archiveSearchLower, archiveSort]);
  const archiveItemsGroupedByPeriod = useMemo(() => {
    const now = Date.now();
    const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;
    const cardsInPeriod =
      archiveFilterType === 'lists'
        ? []
        : filteredArchivedCards.filter(
            (a) => new Date(a.archivedAt).getTime() >= sevenDaysAgo,
          );
    const listsInPeriod =
      archiveFilterType === 'cards'
        ? []
        : filteredArchivedLists.filter(
            (a) => new Date(a.archivedAt).getTime() >= sevenDaysAgo,
          );
    return { cardsInPeriod, listsInPeriod };
  }, [archiveFilterType, filteredArchivedCards, filteredArchivedLists]);

  const createBoardForPicker = useCallback(async () => {
    if (!companyId || creatingBoard) return;
    setCreatingBoard(true);
    try {
      const created = await createBoard('New board', companyId);
      const authUserId =
        profile &&
        (typeof profile.authUser === 'string'
          ? profile.authUser
          : profile.authUser?.id);
      if (authUserId) {
        try {
          await addBoardMember(
            created.id,
            { userId: authUserId, name: profile.username },
            companyId,
          );
        } catch (memberErr) {
          const memberMsg =
            memberErr instanceof Error ? memberErr.message : String(memberErr);
          if (!/already|duplicate|exists/i.test(memberMsg)) {
            showToast(
              'Board created but adding you as a member failed.',
              'error',
            );
          }
        }
      }
      // Re-fetch from backend: never merge client-side board lists.
      await loadBoards();
      setActiveBoardId(created.id);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to create board';
      setBoardsError(msg);
      showToast(msg, 'error');
    } finally {
      setCreatingBoard(false);
    }
  }, [
    companyId,
    creatingBoard,
    profile,
    setActiveBoardId,
    showToast,
    loadBoards,
  ]);

  const createLabelForCard = useCallback(
    async (
      boardId: string,
      label: { name: string; color: string },
      companyIdParam: string,
    ): Promise<{ id: string; name: string; color: string } | null> => {
      const nameKey = label.name.trim().toLowerCase();
      const colorKey = label.color.trim().toLowerCase();
      const inFlightKey = `${boardId}|${companyIdParam}|${nameKey}|${colorKey}`;

      const cached = labelCreatedCacheRef.current.get(inFlightKey);
      if (cached) return cached;

      const existingPromise = labelCreateInFlightRef.current.get(inFlightKey);
      if (existingPromise) return existingPromise;

      const tempId = `temp-label-${Date.now()}`;
      const optimisticLabel: KanbanLabel = {
        id: tempId,
        name: label.name,
        color: label.color,
      };

      // Optimistically add label to the board-level labels list.
      setBoardLabels((prev) => [...prev, optimisticLabel]);

      const promise = (async () => {
        try {
          const created = await createLabel(
            boardId,
            { name: label.name, color: label.color },
            companyIdParam,
          );
          // Replace the temporary label with the real one from the server.
          setBoardLabels((prev) => {
            const mapped = prev.map((l) => (l.id === tempId ? created : l));
            // Socket may have already inserted the real label; ensure we only
            // keep one entry per `id`.
            const seen = new Set<string>();
            return mapped.filter((l) => {
              if (seen.has(l.id)) return false;
              seen.add(l.id);
              return true;
            });
          });
          if (created) {
            labelCreatedCacheRef.current.set(inFlightKey, created);
          }
          return created;
        } catch (err) {
          const msg =
            err instanceof Error ? err.message : 'Failed to create label';
          // Roll back the optimistic label.
          setBoardLabels((prev) => prev.filter((l) => l.id !== tempId));
          showToast(msg, 'error');
          return null;
        } finally {
          labelCreateInFlightRef.current.delete(inFlightKey);
        }
      })();

      labelCreateInFlightRef.current.set(inFlightKey, promise);
      return promise;
    },
    [showToast],
  );

  const updateLabelForCard = useCallback(
    async (
      boardId: string,
      labelId: string,
      updates: { name?: string; color?: string },
      companyIdParam: string,
    ): Promise<{ id: string; name: string; color: string } | null> => {
      const prevLabels = boardLabels;
      setBoardLabels((prev) =>
        prev.map((l) => (l.id === labelId ? { ...l, ...updates } : l)),
      );
      try {
        const updated = await updateLabel(
          boardId,
          labelId,
          { name: updates.name, color: updates.color },
          companyIdParam,
        );
        setBoardLabels((prev) =>
          prev.map((l) => (l.id === labelId ? updated : l)),
        );
        return updated;
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : 'Failed to update label';
        setBoardLabels(prevLabels);
        showToast(msg, 'error');
        return null;
      }
    },
    [boardLabels, showToast],
  );

  const deleteLabelForCard = useCallback(
    async (
      boardId: string,
      labelId: string,
      companyIdParam: string,
    ): Promise<void> => {
      const prevLabels = boardLabels;
      const prevCards = cards;
      // Optimistic remove: keep label order for remaining labels and
      // strip deleted label from all cards.
      setBoardLabels((prev) => prev.filter((l) => l.id !== labelId));
      setCards((prev) => {
        const next: Record<string, KanbanCardData> = {};
        Object.entries(prev).forEach(([id, card]) => {
          const currentLabelIds = card.labelIds ?? [];
          if (!currentLabelIds.includes(labelId)) {
            next[id] = card;
            return;
          }
          next[id] = {
            ...card,
            labelIds: currentLabelIds.filter((idValue) => idValue !== labelId),
          };
        });
        return next;
      });
      try {
        await deleteLabelApi(boardId, labelId, companyIdParam);
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : 'Failed to delete label';
        setBoardLabels(prevLabels);
        setCards(prevCards);
        showToast(msg, 'error');
      }
    },
    [boardLabels, cards, showToast],
  );

  return {
    companyId,
    activeBoardId,
    activeBoard,
    setActiveBoardId,
    boards,
    boardsLoading,
    boardsError,
    creatingBoard,
    boardDataLoading,
    boardDataError,
    loadBoardData,
    columns,
    cards,
    boardLabels,
    boardMembers,
    editingBoardTitle,
    setEditingBoardTitle,
    boardTitleDraft,
    setBoardTitleDraft,
    handleBoardTitleClick,
    handleBoardTitleSubmit,
    boardMenuButtonRef,
    boardMenuOpen: boardMenuOpen,
    setBoardMenuOpen,
    archiveCount,
    boardWrapperRef,
    sensors,
    collisionDetection,
    handleDragStart,
    handleDragOver,
    handleDragCancel,
    handleDragEnd,
    handleBoardMouseDown,
    handleBoardMouseMove,
    handleBoardMouseUp,
    handleBoardMouseLeave,
    addingColumn,
    newColumnTitle,
    setNewColumnTitle,
    handleAddColumnClick,
    handleAddColumnConfirm,
    handleAddColumnCancel,
    activeId,
    activeCard,
    activeListColumn,
    selectedCardId,
    setSelectedCardId: setSelectedCardIdWrapped,
    commentIdToScroll: urlCommentId,
    onCopyCardLink,
    onCopyCommentLink,
    selectedCard,
    selectedColumnTitle,
    setRenameCardId,
    renameCardId,
    editingColumnId,
    setEditingColumnId,
    editingColumnTitle,
    setEditingColumnTitle,
    editingCardTitleId,
    setEditingCardTitleId,
    editingCardTitleValue,
    setEditingCardTitleValue,
    addingCardColumnId,
    newCardTitle,
    setNewCardTitle,
    handleColumnTitleClick,
    handleColumnTitleSubmit,
    handleAddCardClick,
    handleAddCardConfirm,
    handleAddCardCancel,
    handleOpenListMenu,
    handleOpenCardMenu,
    handleCardClick,
    handleUpdateCard,
    handleBoardCardTitleSubmit,
    handleBoardCardTitleCancel,
    handleAddComment,
    handleMoveCard,
    getBoardListsForMove,
    listMenuAnchor,
    listMenuView,
    setListMenuAnchor,
    setListMenuView,
    copyListName,
    setCopyListName,
    moveListPosition,
    setMoveListPosition,
    moveAllCardsTargetId,
    setMoveAllCardsTargetId,
    sortByOption,
    setSortByOption,
    handleListMenuAction,
    handleCopyListCreate,
    handleMoveListToPosition,
    handleMoveAllCardsToTarget,
    handleSortListBy,
    handleArchiveList,
    handleArchiveListWithConfirm,
    handleConfirmArchiveList,
    archiveListConfirmColumnId,
    setArchiveListConfirmColumnId,
    handleArchiveAllCardsInList,
    cardMenuAnchor,
    cardMenuView,
    setCardMenuAnchor,
    setCardMenuView,
    handleArchiveCard,
    showArchivedPanel,
    setShowArchivedPanel,
    showMembersPanel,
    setShowMembersPanel,
    archiveSearch,
    setArchiveSearch,
    archiveFilterType,
    setArchiveFilterType,
    archiveSort,
    setArchiveSort,
    archiveItemsGroupedByPeriod,
    filteredArchivedCards,
    filteredArchivedLists,
    handleRestoreCard,
    handleRestoreCardWithTarget,
    handleRestoreList,
    setDeleteConfirmItem,
    deleteConfirmItem,
    handleConfirmPermanentDelete,
    restoreCardItem,
    setRestoreCardItem,
    restoreTargetListId,
    setRestoreTargetListId,
    boardToDelete,
    setBoardToDelete,
    handleConfirmDeleteBoard,
    addMemberEmail,
    setAddMemberEmail,
    addingMember,
    memberToRemove,
    setMemberToRemove,
    handleLinkUserToBoard,
    handleConfirmRemoveBoardMember,
    toast,
    setToast,
    createBoardForPicker,
    createLabel: createLabelForCard,
    updateLabel: updateLabelForCard,
    deleteLabel: deleteLabelForCard,
  };
}
